import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE, iframeClientParam } from '../lib/api';
import { useToast } from '../components/Toast';
import DocumentPickerModal, { type PickedFile } from '../components/DocumentPickerModal';
import { Eye, Trash2, Landmark, ChevronDown, ChevronRight, FileText, Link2, Check, X, Zap, Search, Tag, Download, FilePlus, CreditCard, AlertTriangle, Ban, CheckCircle2 } from 'lucide-react';
import ContinuityChain from '../components/ContinuityChain';
import { useDateFilter } from '../contexts/DateFilterContext';
import { useAuth } from '../contexts/AuthContext';
import SupervisorPasswordModal from '../components/SupervisorPasswordModal';
import AutoMatchReviewModal from '../components/AutoMatchReviewModal';
import AuditTrailModal from '../components/AuditTrailModal';
import { tr } from '../lib/i18nHelpers';
import { stageOf } from '../lib/lifecycleStepper';
import { postingStatus, type StatementPostingSummary } from '../lib/statementPostingStatus';
import { rowPostingView } from '../lib/statementRowPosting';
import LifecycleStepper from '../components/LifecycleStepper';
import { promptReviewedVariance, promptUndoReason } from '../lib/matching';
import { buildCoaTree, CoaNode, isTemporaryAccount, NOT_APPLICABLE } from '../lib/coa-hierarchy';
import TxPostingPanel, { PostingLine } from '../components/TxPostingPanel';
import SlideOpen from '../components/SlideOpen';
import { isMissingCoa } from '../lib/coa-flag';
import { useHighlightTarget } from '../hooks/useHighlightTarget';
import { exportBankStatementsXlsx, exportStatementTransactionsXlsx, BankExportRow } from '../lib/export-bank-xlsx';
import { varianceReasonRequired, resolveVarianceReason, VARIANCE_REASONS } from '../lib/varianceReason';
import { useTableControls, Th, TablePager, ColumnMeta } from '../components/table';

const NA_ACCOUNT = NOT_APPLICABLE;

function isMissingBankLink(tx: Transaction): boolean {
  return !tx.invoice_id && !tx.card_statement_id && !(tx.linked_invoices || []).length;
}

function compareBankLinks(a: Transaction, b: Transaction): number {
  const missingRank = Number(isMissingBankLink(b)) - Number(isMissingBankLink(a));
  if (missingRank !== 0) return missingRank;

  const aOrder = typeof a.sort_order === 'number' ? a.sort_order : Number.MAX_SAFE_INTEGER;
  const bOrder = typeof b.sort_order === 'number' ? b.sort_order : Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;

  return String(a.transaction_date || '').localeCompare(String(b.transaction_date || ''));
}

function PreFillButton({ item, bankCode, onPrefillJe, onPrefillMatch }: {
  item: any;
  bankCode: string;
  onPrefillJe: (txId: string, bankCode: string, lines: any[]) => void;
  onPrefillMatch: (txId: string, invoiceNumber?: string, confidence?: string) => void;
}) {
  if (item.kind === 'invoice_match')
    return (
      <button data-testid="review-prefill" onClick={() => onPrefillMatch(item.transaction_id, item.prefill?.invoice_number, item.confidence)}
        className="px-2 py-0.5 text-xs rounded border hover:bg-green-100 whitespace-nowrap">
        {tr('Review match', '審視配對', '审视配对')}
      </button>
    );
  return (
    <button data-testid="review-prefill" onClick={() => onPrefillJe(item.transaction_id, bankCode, item.prefill?.lines || [])}
      className="px-2 py-0.5 text-xs rounded border hover:bg-green-100 whitespace-nowrap">
      {tr('Pre-fill', '預填', '预填')}
    </button>
  );
}

interface Transaction {
  id: string;
  transaction_date: string;
  description: string;
  deposit_amount: number;
  withdrawal_amount: number;
  balance: number;
  account_type: string;
  account_code?: string | null;
  reference: string | null;
  payee_payer?: string | null;
  cheque_no?: string | null;
  sort_order: number;
  invoice_id?: string | null;
  match_confidence?: string | null;
  match_status?: string;
  invoice_number?: string | null;
  invoice_total?: number | null;
  invoice_status?: string | null;
  card_statement_id?: string | null;
  card_issuer?: string | null;
  cs_statement_year?: number | null;
  cs_statement_month?: number | null;
  cs_closing_balance?: number | null;
  linked_invoices?: { invoice_id: string; invoice_number: string; allocated_amount: number | null }[] | null;
  payment_entry_number?: string | null;
}

// Payee/payer + cheque no. cell. Value semantics: NULL = unknown (blank, the
// user can type one), 'N/A' = explicitly not applicable, any other string = the
// value (LLM-extracted when the statement prints it).
function PayeeCell({ value, editing, placeholder, onChange }: {
  value: string | null | undefined;
  editing: boolean;
  placeholder?: string;
  onChange?: (v: string | null) => void;
}) {
  const na = value === 'N/A';
  if (!editing) {
    if (na) return (
      <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border font-medium text-muted-foreground"
        title={tr('Not applicable', '不適用', '不适用')}>N/A</span>
    );
    if (!value) return <span className="text-muted-foreground">—</span>;
    return <span className="truncate block" title={value}>{value}</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {na ? (
        <span className="flex-1 text-center text-[10px] px-1.5 py-1 rounded bg-muted border border-border font-medium text-muted-foreground">N/A</span>
      ) : (
        <input value={value || ''} placeholder={placeholder || '—'}
          onChange={e => onChange?.(e.target.value.trim() || null)}
          className="w-full min-w-0 px-1 py-0.5 border rounded text-xs bg-background" />
      )}
      <button type="button"
        onClick={() => onChange?.(na ? null : 'N/A')}
        title={na
          ? tr('Clear Not-Applicable — type a value instead', '清除不適用——改為輸入值', '清除不适用——改为输入值')
          : tr('Mark as not applicable', '標記為不適用', '标记为不适用')}
        className={`shrink-0 px-1 py-0.5 rounded text-xs border font-semibold ${
          na ? 'text-primary border-primary/40 bg-primary/5' : 'text-muted-foreground border-border hover:bg-muted'
        }`}>Ø</button>
    </div>
  );
}

interface ManualTxRow {
  transaction_date: string;
  description: string;
  deposit_amount: number;
  withdrawal_amount: number;
  balance: number;
  reference: string;
}

function ManualBankStatementEditor({ onSave, onCancel }: { onSave: (data: any) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [currency, setCurrency] = useState('HKD');
  const [openingBalance, setOpeningBalance] = useState<number | ''>('');
  const [closingBalance, setClosingBalance] = useState<number | ''>('');
  const [sourceFileId, setSourceFileId] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickedFileName, setPickedFileName] = useState<string | null>(null);
  const [txRows, setTxRows] = useState<ManualTxRow[]>([
    { transaction_date: '', description: '', deposit_amount: 0, withdrawal_amount: 0, balance: 0, reference: '' },
  ]);

  const addRow = () => setTxRows([...txRows, { transaction_date: '', description: '', deposit_amount: 0, withdrawal_amount: 0, balance: 0, reference: '' }]);
  const removeRow = (idx: number) => setTxRows(txRows.filter((_, i) => i !== idx));

  const updateRow = (idx: number, field: keyof ManualTxRow, value: any) => {
    const rows = [...txRows];
    (rows[idx] as any)[field] = value;
    if (field === 'deposit_amount' && (value as number) > 0) rows[idx].withdrawal_amount = 0;
    if (field === 'withdrawal_amount' && (value as number) > 0) rows[idx].deposit_amount = 0;
    // Auto-fill balance
    if (field === 'deposit_amount' || field === 'withdrawal_amount' || field === 'balance') {
      if (idx > 0) {
        const prevBalance = rows[idx - 1].balance || 0;
        rows[idx].balance = prevBalance + (rows[idx].deposit_amount || 0) - (rows[idx].withdrawal_amount || 0);
      }
    }
    setTxRows(rows);
  };

  const canSave = bankName.trim() && txRows.length > 0 && txRows.every(tx => tx.transaction_date && tx.description);

  return (
    <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
      <h3 className="font-semibold text-sm">{tr('Manual Bank Statement Entry', '手動銀行月結單輸入', '手动银行月结单输入')}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium">{tr('Bank Name', '銀行名稱', '银行名称')} *</label>
          <input value={bankName} onChange={e => setBankName(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" placeholder="HSBC" />
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Account Number', '賬號', '账号')}</label>
          <input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Year', '年份', '年份')}</label>
          <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Month', '月份', '月份')}</label>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} className="w-full border rounded px-2 py-1 text-sm">
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Currency', '貨幣', '货币')}</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
            <option value="HKD">HKD</option><option value="USD">USD</option><option value="CNY">CNY</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Opening Balance', '期初餘額', '期初余额')}</label>
          <input type="number" step="0.01" value={openingBalance} onChange={e => setOpeningBalance(e.target.value ? Number(e.target.value) : '')} className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Closing Balance', '期末餘額', '期末余额')}</label>
          <input type="number" step="0.01" value={closingBalance} onChange={e => setClosingBalance(e.target.value ? Number(e.target.value) : '')} className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">{tr('Attach File (optional)', '附加文件（可選）', '附加文件（可选）')}</label>
          {pickedFileName ? (
            <div className="flex items-center gap-1 text-sm bg-background border rounded px-2 py-1">
              <span className="truncate">{pickedFileName}</span>
              <button onClick={() => { setSourceFileId(null); setPickedFileName(null); }} className="text-destructive text-xs ml-1">✕</button>
            </div>
          ) : (
            <button onClick={() => setShowPicker(true)} type="button" className="w-full text-left border rounded px-2 py-1 text-sm text-muted-foreground hover:bg-background">
              + {tr('Choose file', '選擇文件', '选择文件')}
            </button>
          )}
        </div>
      </div>

      {/* Transaction grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 px-1 w-28">{tr('Date', '日期', '日期')} *</th>
              <th className="py-1 px-1">{tr('Description', '描述', '描述')} *</th>
              <th className="py-1 px-1 w-28 text-right">{tr('Deposit', '存入', '存入')}</th>
              <th className="py-1 px-1 w-28 text-right">{tr('Withdrawal', '支出', '支出')}</th>
              <th className="py-1 px-1 w-28 text-right">{tr('Balance', '餘額', '余额')}</th>
              <th className="py-1 px-1 w-24">{tr('Ref', '參考', '参考')}</th>
              <th className="py-1 px-1 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {txRows.map((tx, idx) => (
              <tr key={idx} className="border-b">
                <td className="py-1 px-1"><input type="date" value={tx.transaction_date} onChange={e => updateRow(idx, 'transaction_date', e.target.value)} className="w-full border rounded px-1 py-0.5 text-xs" /></td>
                <td className="py-1 px-1"><input value={tx.description} onChange={e => updateRow(idx, 'description', e.target.value)} className="w-full border rounded px-1 py-0.5 text-xs" /></td>
                <td className="py-1 px-1"><input type="number" step="0.01" value={tx.deposit_amount || ''} onChange={e => updateRow(idx, 'deposit_amount', Number(e.target.value) || 0)} className="w-full border rounded px-1 py-0.5 text-xs text-right" /></td>
                <td className="py-1 px-1"><input type="number" step="0.01" value={tx.withdrawal_amount || ''} onChange={e => updateRow(idx, 'withdrawal_amount', Number(e.target.value) || 0)} className="w-full border rounded px-1 py-0.5 text-xs text-right" /></td>
                <td className="py-1 px-1"><input type="number" step="0.01" value={tx.balance} onChange={e => updateRow(idx, 'balance', Number(e.target.value) || 0)} className="w-full border rounded px-1 py-0.5 text-xs text-right" /></td>
                <td className="py-1 px-1"><input value={tx.reference} onChange={e => updateRow(idx, 'reference', e.target.value)} className="w-full border rounded px-1 py-0.5 text-xs" /></td>
                <td className="py-1 px-1">{txRows.length > 1 && <button onClick={() => removeRow(idx)} className="text-destructive text-xs">✕</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={addRow} className="text-xs text-primary hover:underline">+ {tr('Add Row', '新增列', '新增列')}</button>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 border rounded-md text-sm hover:bg-muted">{tr('Cancel', '取消', '取消')}</button>
        <button onClick={() => {
          onSave({
            bank_name: bankName, account_number: accountNumber, currency,
            statement_year: year, statement_month: month,
            opening_balance: openingBalance || null, closing_balance: closingBalance || null,
            source_file_id: sourceFileId,
            transactions: txRows.map((tx, i) => ({ ...tx, sort_order: i })),
          });
        }} disabled={!canSave}
          className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50">
          {tr('Save & Review', '儲存並審核', '储存并审核')}
        </button>
      </div>

      {showPicker && (
        <DocumentPickerModal
          alreadyPicked={[]}
          onPick={(picked: PickedFile[]) => {
            if (picked[0]) {
              setSourceFileId(picked[0].id);
              setPickedFileName(picked[0].filename);
            }
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

const STATEMENT_COLS: ColumnMeta[] = [
  { key: '_period', type: 'text' },
  { key: 'status', type: 'text' },
  { key: 'closing_balance', type: 'number' },
  { key: '_unlinked', type: 'number' },
];

interface StatementGroupTableProps {
  statements: any[];
  activeFilter: string | null;
  isStaff: boolean;
  t: (key: string) => string;
  setExpandedId: (id: string | null) => void;
  setEditMode: (v: boolean) => void;
  setEdits: React.Dispatch<React.SetStateAction<Record<string, Partial<Transaction>>>>;
  setLocalRows: React.Dispatch<React.SetStateAction<Transaction[]>>;
  setSupModal: React.Dispatch<React.SetStateAction<{ show: boolean; onConfirm: () => void } | null>>;
  deleteMut: { mutate: (id: string) => void };
}

function StatementGroupTable({
  statements, activeFilter, isStaff, t,
  setExpandedId, setEditMode, setEdits, setLocalRows, setSupModal, deleteMut,
}: StatementGroupTableProps) {
  const rows = useMemo(
    () => statements.map((s: any) => ({
      ...s,
      _period: `${s.statement_year}-${String(s.statement_month).padStart(2, '0')}`,
      _unlinked: s.unlinked_count || 0,
    })),
    [statements],
  );
  const controls = useTableControls({ rows, columns: STATEMENT_COLS });

  return (
    <div>
      {controls.filteredCount === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">
          {tr('No statements match the filters', '沒有符合篩選的月結單', '没有符合筛选的月结单')}
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
            <Th controls={controls} colKey="_period">{tr('Period', '期間', '期间')}</Th>
            <Th controls={controls} colKey="status">{tr('Status', '狀態', '状态')}</Th>
            <Th controls={controls} colKey="closing_balance" align="right">{tr('Closing Balance', '期末餘額', '期末余额')}</Th>
            <Th controls={controls} colKey="_unlinked" align="right">{tr('Unmatched', '未配對', '未配对')}</Th>
            <th className="p-3 text-right font-medium">{tr('Actions', '操作', '操作')}</th>
          </tr>
        </thead>
        <tbody>
          {controls.visibleRows.map((s: any) => (
            <tr key={s.id} className="border-b hover:bg-muted/30 transition-colors">
              <td className="p-3 align-top">
                <div id={`stmt-row-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {s._period} {s.bank_name || 'Statement'}
                    </span>
                    {s.account_type && (
                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{s.account_type}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {s.account_number && <span>{s.account_number}</span>}
                    {s.branch && <span className="text-muted-foreground/60">{s.branch}</span>}
                    {s.currency && <span className="font-mono">{s.currency}</span>}
                  </div>
                </div>
              </td>
              <td className="p-3 align-top">
                <LifecycleStepper
                  type="document"
                  stage={stageOf('document', { lifecycle: s.lifecycle, status: s.status })}
                  variant="compact"
                />
              </td>
              <td className="p-3 align-top text-right">
                {s.closing_balance != null && (
                  <span className={`font-mono font-medium ${s.closing_balance >= 0 ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
                    {s.closing_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                )}
              </td>
              <td className="p-3 align-top text-right">
                {activeFilter === 'unmatched' && s.unlinked_count > 0 && (
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium px-2 py-0.5 rounded-full">
                    {s.unlinked_count} {tr('unreconciled', '未對賬', '未对账')}
                  </span>
                )}
                {activeFilter !== 'unmatched' && s.tx_count > 0 && s.unlinked_count > 0 && (
                  <span className="text-xs text-amber-900 dark:text-amber-100 font-medium">
                    ⚠ {s.unlinked_count}/{s.tx_count} unlinked ({Math.round(s.unlinked_count / s.tx_count * 100)}%)
                  </span>
                )}
                {activeFilter !== 'unmatched' && s.tx_count > 0 && s.unlinked_count === 0 && (
                  <span className="text-xs text-green-900 dark:text-green-200 font-medium">✓ All linked</span>
                )}
              </td>
              <td className="p-3 align-top text-right">
                <div className="flex gap-2 justify-end items-center" onClick={e => e.stopPropagation()}>
                  {s.balance_status === 'mismatch' && <span className="text-xs text-red-900 dark:text-red-200 font-medium" title="Balance mismatch — confirmed with unresolved difference">⚠</span>}
                  {s.balance_status === 'corrected' && <span className="text-xs text-blue-600 font-medium" title="AI data corrected manually">✏</span>}
                  {/* Eye = view transaction record (modal). View PDF / Review
                      Extracted Data / Edit live inside the record. */}
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setEdits({});
                      setLocalRows([]);
                      setExpandedId(s.id);
                    }}
                    className="p-1.5 hover:bg-muted rounded"
                    title={tr('View transaction record', '查看交易記錄', '查看交易记录')}
                    aria-label={tr('View transaction record', '查看交易記錄', '查看交易记录')}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button onClick={() => {
                    const doDelete = () => deleteMut.mutate(s.id);
                    if (isStaff) {
                      setSupModal({ show: true, onConfirm: doDelete });
                    } else {
                      if (confirm(t('common.confirmDelete'))) doDelete();
                    }
                  }}
                    className="p-1.5 hover:bg-muted rounded text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <TablePager controls={controls} />
    </div>
  );
}

export default function BankStatements() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff' || user?.role === 'viewer';
  const { startDate, endDate } = useDateFilter();
  const [supModal, setSupModal] = useState<{ show: boolean; onConfirm: () => void } | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Statement list is grouped by bank account (collapsed by default); this holds
  // the account keys the user has opened.
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightStmtId = searchParams.get('highlight') || null;
  const activeFilter = searchParams.get('filter') || null;
  const showChain = searchParams.get('view') === 'chain';
  const [matchTxId, setMatchTxId] = useState<string | null>(null);
  const [auditTx, setAuditTx] = useState<{ statementName: string | null; transactionDate: string; description: string; amount: number; matchStatus: string; invoiceIds: string[]; txId: string; isDeposit: boolean } | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, Partial<Transaction>>>({});
  const [localRows, setLocalRows] = useState<Transaction[]>([]);
  const [acctModalTx, setAcctModalTx] = useState<Transaction | null>(null);
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [reconData, setReconData] = useState<any>(null);
  const [prefillTxId, setPrefillTxId] = useState<string | null>(null);
  const [prefilledLines, setPrefilledLines] = useState<PostingLine[] | undefined>(undefined);
  // Default OFF: balance_status='ok' only means the balance math checked out at
  // import — it does NOT mean the statement is reconciled. Hiding COA controls by
  // default blocked account assignment on every auto-verified statement (2026-08-17).
  const [hideReconciledCoa, setHideReconciledCoa] = useState(false);
  const [autoMatchResults, setAutoMatchResults] = useState<any[] | null>(null);
  // Card matching queued behind the invoice review modal — the combined
  // "Match Invoices & Cards" button runs invoices first, cards right after
  // the invoice review closes (or immediately when invoices find nothing).
  const [pendingCards, setPendingCards] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [cardMatchResults, setCardMatchResults] = useState<any[] | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const highlightTxRef = React.useRef<string | null>(null);
  const defaultFilterAppliedRef = React.useRef(false);
  const { highlight: highlightId, highlightTx } = useHighlightTarget();
  const highlightTargetRef = React.useRef<string | null>(null);
  // ?statement=&focus=1 (review page "← Back to Bank Statements"): hold the id
  // until the list query lands, then run the reveal → ring → record choreography.
  const chainFocusRef = useRef<string | null>(null);
  const chainFocusFiredRef = useRef<string | null>(null);

  // Auto-expand statement and highlight transaction from query params
  React.useEffect(() => {
    const statementId = searchParams.get('statement');
    const highlightId = searchParams.get('highlight');
    if (!statementId) return;
    if (searchParams.get('focus') === '1') {
      // Reveal choreography first — the effect below fires it once the list loads
      chainFocusRef.current = statementId;
      return;
    }
    setExpandedId(statementId);
    // &edit=1 (review page "Edit Transactions") opens the record in edit mode
    if (searchParams.get('edit') === '1') {
      setEditMode(true);
      setEdits({});
      setLocalRows([]);
    }
    highlightTxRef.current = highlightId;
  }, []); // Run once on mount

  // After scrolling/highlighting, clear the URL params
  React.useEffect(() => {
    if (!searchParams.has('statement') && !searchParams.has('highlight') && !searchParams.has('edit')) return;
    const timer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      if (newParams.has('highlight') || newParams.has('statement') || newParams.has('edit')) {
        newParams.delete('highlight');
        newParams.delete('statement');
        newParams.delete('edit');
        setSearchParams(newParams, { replace: true });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['bank-statements', startDate, endDate],
    queryFn: () => {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      return api(`/bank-statements?${params.toString()}`);
    },
  });

  const { data: accountsData } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api('/bookkeeping/accounts'),
  });
  const accounts: any[] = accountsData?.data || [];
  // Hierarchical COA: parents = group headers (not postable), leaves selectable.
  // Zero-stripped-stem rule shared with the backend PATCH guard + review page.
  const coaTree: CoaNode[] = useMemo(() => buildCoaTree(accounts), [accounts]);
  // Shared <option> list for the account-column pickers (parents = disabled groups)
  const coaOptions = coaTree.map(n => n.isParent ? (
    <option key={n.account.account_code} value="" disabled className="text-muted-foreground">
      {`${' '.repeat(n.depth * 2)}${n.account.account_code} ${n.account.account_name}`}
    </option>
  ) : (
    <option key={n.account.account_code} value={n.account.account_code}>
      {`${' '.repeat(n.depth * 3)}${n.account.account_code} ${n.account.account_name}`}
    </option>
  ));

  const detailQuery = useQuery({
    queryKey: ['bank-statement', expandedId],
    queryFn: () => api(`/bank-statements/${expandedId}`),
    enabled: !!expandedId,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/bank-statements/${id}`, { method: 'DELETE' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['bank-continuity'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transactions-flat'] });
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      setExpandedId(null);
      const cascadeMsg = data?.transactions_deleted != null
        ? `\n${data.transactions_deleted} transaction${data.transactions_deleted === 1 ? '' : 's'} also removed.`
        : '';
      const fileMsg = data?.file_deleted ? '\nOriginal PDF also removed from File Storage.' : '';
      const restoreMsg = data?.restorable_until
        ? '\n\nItem moved to Recycle Bin — can be restored within 30 days.'
        : '';
      if (cascadeMsg || fileMsg || restoreMsg) {
        setTimeout(() => toast.info(`Statement deleted.${cascadeMsg}${fileMsg}${restoreMsg}`), 10);
      }
    },
    onError: (err: any) => {
      if (err?.status === 403 || /higher permission/i.test(err?.error || err?.message || '')) {
        toast.info('Delete not allowed for your account. Only account owner or boss-level users can delete records. Please ask your admin.');
      } else {
        toast.info(`Delete failed: ${err?.error || err?.message || 'Unknown error'}`);
      }
    },
  });

  // Fires the queued card matching once, from whichever finisher applies.
  const runPendingCards = () => {
    if (!pendingCards) return;
    setPendingCards(false);
    autoMatchCardsMut.mutate();
  };

  // UI Auto-Match uses the RULES pipeline. The hybrid matcher is exposed only
  // as the API preview POST /bank-statements/auto-match-hybrid and has no UI
  // caller — don't label it as a live user-facing fallback until wired here.
  const autoMatchMut = useMutation({
    mutationFn: () => api('/bank-statements/auto-match', { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      if (data.matched?.length > 0) {
        setAutoMatchResults(data.matched);
      } else {
        toast.info(tr('No invoice matches found. All deposits are either already matched or have no invoice candidates.', '沒有找到發票配對。所有存款要麼已配對，要麼沒有發票候選。', '没有找到发票配对。所有存款要么已配对，要么没有发票候选。'));
        runPendingCards();
      }
    },
    onError: () => setPendingCards(false),
  });

  const confirmMatchMut = useMutation({
    mutationFn: ({ txId, invoiceId, invoiceIds, varianceAmount, reviewReason }: {
      txId: string; invoiceId: string | null; invoiceIds?: string[];
      varianceAmount?: number; reviewReason?: string;
    }) =>
      api(`/bank-statements/transactions/${txId}/match`, {
        method: 'PATCH',
        body: {
          ...(invoiceIds?.length
            ? { invoice_ids: invoiceIds }
            : { invoice_id: invoiceId }),
          action: 'confirm',
          ...(varianceAmount !== undefined ? { variance_amount: varianceAmount } : {}),
          ...(reviewReason ? { review_reason: reviewReason } : {}),
        },
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      queryClient.invalidateQueries({ queryKey: ['entries'] }); // GL payment posted server-side by the confirm endpoint
      if (data?.coa_notice) toast.success(data.coa_notice);
    },
    onError: (err: any, vars) => {
      const reviewed = !vars.reviewReason && promptReviewedVariance(err);
      if (reviewed) {
        confirmMatchMut.mutate({ ...vars, ...reviewed });
        return;
      }
      toast.error(err?.error || err?.message || tr('Confirm failed', '確認失敗', '确认失败'));
    },
  });

  const unlinkMut = useMutation({
    mutationFn: ({ txId, reason }: { txId: string; reason: string }) =>
      api(`/bank-statements/transactions/${txId}/match`, {
        method: 'PATCH',
        body: { action: 'unlink', reason },
      }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] }); },
  });

  const createInvoiceMut = useMutation({
    mutationFn: (txId: string) => api('/invoices/generate-from-transaction', { method: 'POST', body: { transaction_id: txId } }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices?highlight=${data.id}`);
    },
    onError: (err: any) => {
      toast.info(`Could not create invoice: ${err?.error || err?.message || 'Unknown error'}`);
    },
  });

  const autoCatMut = useMutation({
    mutationFn: () => api(`/bank-statements/${expandedId}/auto-categorize`, { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      toast.info(tr(`Auto-categorized: ${data.categorized} rows, skipped ${data.skipped} (total ${data.total})`, `已自動分類：${data.categorized} 筆，跳過 ${data.skipped} 筆（共 ${data.total} 筆）`, `已自动分類：${data.categorized} 笔，跳過 ${data.skipped} 笔（共 ${data.total} 笔）`));
    },
  });

  const updateTxMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      api(`/bank-statements/transactions/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
    },
  });

  const createTxMut = useMutation({
    mutationFn: (body: any) => api(`/bank-statements/${detail?.id}/transactions`, { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
    },
  });

  const postMissingMut = useMutation({
    mutationFn: (stmtId: string) => api(`/bank-statements/${stmtId}/post-missing`, { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      queryClient.invalidateQueries({ queryKey: ['bank-lifecycle', expandedId] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      if (data?.skipped_duplicate) {
        toast.info(tr(
          'Not posted: this file is already recorded by another statement.',
          '未過賬：此檔案已由另一張月結單記錄。',
          '未过账：此档案已由另一张月结单记录。',
        ));
      } else if ((data?.created || 0) > 0) {
        toast.success(tr(
          `${data.created} missing entr${data.created === 1 ? 'y' : 'ies'} posted.`,
          `已補記 ${data.created} 筆分錄。`,
          `已补记 ${data.created} 笔分录。`,
        ));
      } else {
        toast.info(tr('No missing entries to post.', '沒有需要補記的分錄。', '没有需要补记的分录。'));
      }
    },
    onError: (err: any) => {
      toast.error(err?.message || err?.error || tr('Failed to post missing entries', '補記分錄失敗', '补记分录失败'));
    },
  });

  const saveTxPosting = async (txId: string, movement: number, lines?: PostingLine[], reset?: boolean) => {
    await api(`/bank-statements/transactions/${txId}/posting`, {
      method: 'PUT',
      body: reset ? { reset_to_auto: true } : { lines, movement_amount: movement },
    });
    queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
    setPrefillTxId(null);
    setPrefilledLines(undefined);
  };

  // ── Not-Applicable rows: dual Dr/Cr pickers in the account column ──
  // Pending picks per tx id; the bank side pre-fills with the statement's own
  // account. Saving when both sides are chosen PATCHes the contra (flips the
  // row out of N/A) and, when the bank side was changed, rewrites the posting
  // with the explicit fixed account so the JE is exactly Dr X / Cr Y.
  const [naSides, setNaSides] = useState<Record<string, { dr: string; cr: string }>>({});
  const [naEditTxId, setNaEditTxId] = useState<string | null>(null);
  const saveNaSides = async (tx: Transaction, sides: { dr: string; cr: string }, movement: number, stmtBankCode: string) => {
    setNaEditTxId(null);
    const isDeposit = (tx.deposit_amount || 0) > 0;
    const contra = isDeposit ? sides.cr : sides.dr;
    const bankSide = isDeposit ? sides.dr : sides.cr;
    setNaSides(prev => { const next = { ...prev }; delete next[tx.id]; return next; });
    // Rows not yet saved to the server (edit-mode additions) go into the edits map
    if (tx.id.startsWith('local-')) {
      setEdits(prev => ({ ...prev, [tx.id]: { ...prev[tx.id], account_code: contra || null, match_status: contra ? 'unmatched' : 'not_required' } }));
      return;
    }
    if (!contra) {
      // contra cleared → back to Not-Applicable (no posting)
      updateTxMut.mutate({ id: tx.id, body: { account_code: null, match_status: 'not_required' } });
      return;
    }
    try {
      await updateTxMut.mutateAsync({ id: tx.id, body: { account_code: contra } });
      if (bankSide && bankSide !== stmtBankCode && movement > 0) {
        await api(`/bank-statements/transactions/${tx.id}/posting`, {
          method: 'PUT',
          body: { lines: [{ account_code: contra, amount: movement }], movement_amount: movement, fixed_account_code: bankSide },
        });
        queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      }
    } catch (e: any) {
      toast.error(e?.message || tr('Save failed', '保存失敗', '保存失败'));
    }
  };

  const unlinkMatchWithReason = (txId: string) => {
    const reason = promptUndoReason();
    if (reason) unlinkMut.mutate({ txId, reason });
  };
  const unlinkMatchAsyncWithReason = async (txId: string) => {
    const reason = promptUndoReason();
    if (!reason) return Promise.resolve();
    return unlinkMut.mutateAsync({ txId, reason });
  };

  // Review-vs-Ledger suggestion → pre-fill the row's posting editor
  const prefillJe = (txId: string, bankCode: string, lines: any[]) => {
    setPrefillTxId(txId);
    setPrefilledLines(
      lines
        .filter((l: any) => l.account_code !== bankCode)
        .map((l: any) => ({ account_code: l.account_code, amount: Math.round(((l.debit || 0) + (l.credit || 0)) * 100) / 100 }))
    );
    toast.info(tr('Suggestion pre-filled — review and save', '建議已預填——請確認後保存', '建议已预填——请确认后保存'));
  };
  // Review-vs-Ledger invoice suggestion → open the existing match modal with a hint
  const prefillMatch = (txId: string, invoiceNumber?: string, confidence?: string) => {
    if (invoiceNumber)
      toast.info(tr(`Suggested: ${invoiceNumber} (${confidence ?? 'low'} confidence)`,
        `建議：${invoiceNumber}（${confidence ?? 'low'} 信心）`, `建议：${invoiceNumber}（${confidence ?? 'low'} 信心）`));
    setMatchTxId(txId);
  };

  const autoMatchCardsMut = useMutation({
    mutationFn: () => api('/bank-statements/auto-match-cards', { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
      if (data.matched?.length > 0) {
        setCardMatchResults(data.matched);
      } else {
        toast.info(tr('No card matches found.', '沒有找到信用卡配對。', '没有找到信用卡配对。'));
      }
    },
  });

  const cardLinkMut = useMutation({
    mutationFn: ({ txId, csId, action }: { txId: string; csId?: string; action: 'link' | 'unlink' }) =>
      api(`/bank-statements/transactions/${txId}/card-link`, {
        method: 'PATCH',
        body: { card_statement_id: csId, action },
      }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] }); },
  });

  const skipLinkMut = useMutation({
    mutationFn: (txId: string) =>
      api(`/bank-statements/transactions/${txId}/skip-link`, { method: 'PATCH' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] }); },
  });

  const statements = (data?.data || []) as any[];

  // Open in link-first mode only when the date-filtered list actually contains
  // unreconciled rows. Clearing the filter removes the param and this one-shot
  // effect does not reapply it.
  React.useEffect(() => {
    if (!statements.length || searchParams.has('filter') || defaultFilterAppliedRef.current) return;
    defaultFilterAppliedRef.current = true;
    if (!statements.some((statement: any) => (statement.unlinked_count || 0) > 0)) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('filter', 'unmatched');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, statements]);

  // Group the list by individual bank account (bank + account number + type +
  // currency); within each account, newest month first.
  const accountKeyOf = (s: any) => `${s.bank_name || ''}|${s.account_number || ''}|${s.account_type || ''}|${s.currency || ''}`;
  const accountGroups = useMemo(() => {
    const map = new Map<string, { key: string; bank_name: string; account_number: string; account_type: string; currency: string; statements: any[] }>();
    for (const s of statements) {
      const key = accountKeyOf(s);
      if (!map.has(key)) map.set(key, { key, bank_name: s.bank_name, account_number: s.account_number, account_type: s.account_type, currency: s.currency, statements: [] });
      map.get(key)!.statements.push(s);
    }
    for (const g of map.values()) {
      g.statements.sort((a, b) => (b.statement_year - a.statement_year) || (b.statement_month - a.statement_month));
    }
    return [...map.values()];
  }, [statements]);

  // Whenever a statement is expanded (deep link, highlight, filter), make sure
  // its account group is open so the row exists to scroll to.
  useEffect(() => {
    if (!expandedId) return;
    const s = statements.find((x: any) => x.id === expandedId);
    if (!s) return;
    const key = accountKeyOf(s);
    setExpandedAccounts(prev => prev.has(key) ? prev : new Set(prev).add(key));
  }, [expandedId, statements]);

  // Continuity-chain month click: reveal + ring-anchor that month's row in the
  // list, then open its transaction record after a short beat. Months outside
  // the current date filter have no row — open the record directly.
  const handleChainSelect = (id: string) => {
    const s = statements.find((x: any) => x.id === id);
    if (!s) {
      setExpandedId(id);
      return;
    }
    setExpandedAccounts(prev => new Set(prev).add(accountKeyOf(s)));
    setTimeout(() => {
      // Ring the inner row card, inset — the group card is overflow-hidden, so
      // an outset ring on the wrapper div gets clipped and looks like nothing.
      const el = document.getElementById(`stmt-row-${id}`)?.firstElementChild as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-inset', 'ring-blue-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-inset', 'ring-blue-400'), 5000);
      }
    }, 150);
    setTimeout(() => setExpandedId(id), 750);
  };

  // Focus choreography (back-from-review): waits for the statements list, then
  // expands the account, rings the row and opens the record after a beat.
  // Lives after `statements`/`handleChainSelect` — deps evaluate during render.
  useEffect(() => {
    const id = chainFocusRef.current;
    if (!id || chainFocusFiredRef.current === id || !statements.length) return;
    chainFocusFiredRef.current = id;
    handleChainSelect(id);
  }, [statements]);

  const highlightFiredRef = useRef<string | null>(null);
  useEffect(() => {
    // ?statement=&highlight=<txId> deep-links (P&L drill-down) are handled by the
    // tx-level effect below — the statement-level highlight must not hijack them.
    if (searchParams.has('statement')) return;
    if (!highlightStmtId || highlightFiredRef.current === highlightStmtId) return;
    setExpandedId(highlightStmtId);
    const tryScroll = (retries: number) => {
      const card = document.getElementById(`stmt-row-${highlightStmtId}`);
      if (card) {
        highlightFiredRef.current = highlightStmtId;
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.classList.add('ring-2', 'ring-blue-400');
        setTimeout(() => card.classList.remove('ring-2', 'ring-blue-400'), 3000);
      } else if (retries > 0) {
        setTimeout(() => tryScroll(retries - 1), 150);
      }
    };
    tryScroll(8);
  }, [highlightStmtId, statements]);

  useEffect(() => {
    if (!showChain) return;
    const timer = setTimeout(() => {
      document.getElementById('statement-continuity-chain')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => clearTimeout(timer);
  }, [showChain]);

  // Statement detail modal: Esc closes
  useEffect(() => {
    if (!expandedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpandedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedId]);

  const filterFiredRef = useRef<boolean>(false);
  useEffect(() => {
    if (activeFilter !== 'unmatched' || filterFiredRef.current || !statements.length) return;
    filterFiredRef.current = true;
    const stmtsWithUnreconciled = statements.filter((s: any) => s.unlinked_count > 0);
    if (stmtsWithUnreconciled.length > 0) {
      setExpandedId(stmtsWithUnreconciled[0].id);
      setTimeout(() => {
        const card = document.getElementById(`stmt-row-${stmtsWithUnreconciled[0].id}`);
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [activeFilter, statements]);

  // Auto-expand and scroll to highlighted transaction from useHighlightTarget
  useEffect(() => {
    if (!highlightId && !highlightTx) return;
    // If it's a statement ID (bs-), expand it directly
    if (highlightId && highlightId.startsWith('bs-')) {
      setExpandedId(highlightId);
      highlightTargetRef.current = highlightTx || highlightId;
      setTimeout(() => {
        const el = document.getElementById(`stmt-row-${highlightId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('highlight-border');
          setTimeout(() => el.classList.remove('highlight-border'), 2500);
        }
      }, 300);
    } else if (highlightId) {
      // It's a transaction ID - store it for when detail loads
      highlightTargetRef.current = highlightId;
    }
  }, [highlightId, highlightTx]);

  const detail = detailQuery.data as any;
  // Title meta for the detail modal: prefer the list row (available instantly);
  // fall back to the detail payload — a continuity-chain click can hit a month
  // outside the current date filter, which has no row in the list.
  const modalMeta = statements.find((x: any) => x.id === expandedId) || detail;
  const transactions = detail?.transactions || [];

  const filteredTransactions = activeFilter === 'unmatched'
    ? transactions.filter((tx: Transaction) =>
        !tx.invoice_id && !tx.card_statement_id &&
        tx.match_status !== 'confirmed' && tx.match_status !== 'skipped' &&
        tx.match_status !== 'suggested' && tx.match_status !== 'not_required'
      )
    : transactions;
  const sortedUnmatchedTransactions = activeFilter === 'unmatched'
    ? [...filteredTransactions].sort(compareBankLinks)
    : filteredTransactions;
  const displayTransactions = activeFilter === 'unmatched' ? sortedUnmatchedTransactions : transactions;
  const modalPostingSummary: StatementPostingSummary | undefined = detail?.posting_summary;
  const modalPostingState = postingStatus(detail?.status, modalPostingSummary);

  // Scroll to highlighted transaction after detail loads (deep-link from P&L drill-down or useHighlightTarget)
  React.useEffect(() => {
    const activeTxId = highlightTxRef.current || highlightTargetRef.current;
    if (!activeTxId) return;
    // If it's a statement ID, it was already handled above
    if (activeTxId.startsWith('bs-')) {
      highlightTxRef.current = null;
      highlightTargetRef.current = null;
      return;
    }
    // For transaction IDs, wait for detail to load then scroll
    if (detail && !detailQuery.isLoading) {
      const txId = activeTxId;
      setTimeout(() => {
        const el = document.getElementById(`tx-row-${txId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('highlight-pulse');
          setTimeout(() => {
            el.classList.remove('highlight-pulse');
          }, 5000);
        }
        highlightTxRef.current = null;
        highlightTargetRef.current = null;
      }, 300);
    }
  }, [detail, detailQuery.isLoading]);

  const totalDeposits = displayTransactions.reduce((s: number, tx: Transaction) => s + tx.deposit_amount, 0);
  const totalWithdrawals = displayTransactions.reduce((s: number, tx: Transaction) => s + tx.withdrawal_amount, 0);
  const suggestedCount = displayTransactions.filter((tx: Transaction) => tx.match_status === 'suggested').length;

  // Excel export: fetch the flat date-range record from the API and generate
  // the workbook client-side (SheetJS). Panel opens prefilled with the global
  // date filter so the default export matches what the page is showing.
  async function exportExcel() {
    if (!exportFrom || !exportTo) {
      toast.error(tr('Please pick both dates', '請選擇開始及結束日期', '请选择开始及结束日期'));
      return;
    }
    setExporting(true);
    try {
      const res = await api(`/bank-statements/export?start_date=${exportFrom}&end_date=${exportTo}`) as any;
      exportBankStatementsXlsx((res?.data ?? []) as BankExportRow[], exportFrom, exportTo);
      toast.success(tr('Excel exported', '已匯出 Excel', '已导出 Excel'));
      setShowExportPanel(false);
    } catch (e: any) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">{t('bank.title')}</h2>
        <p className="text-muted-foreground mt-1">{t('bank.desc')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowManualEntry(!showManualEntry)}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90">
            <FilePlus className="h-4 w-4" />
            {tr('Manual Entry', '手動輸入', '手动输入')}
          </button>
          {/* Tenant-wide matching, same engines+review modals as File Storage:
              invoices first (deposits→AR, withdrawals→AP), then card
              statements right after the invoice review closes. */}
          <button onClick={() => { setPendingCards(true); autoMatchMut.mutate(); }} disabled={autoMatchMut.isPending}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
            <Zap className="h-4 w-4" />
            {autoMatchMut.isPending ? '…' : tr('Match Invoices & Cards', '配對發票及信用卡', '配对发票及信用卡')}
          </button>
          <button onClick={() => {
            if (!showExportPanel) { setExportFrom(startDate || ''); setExportTo(endDate || ''); }
            setShowExportPanel(!showExportPanel);
          }}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-sm font-medium hover:bg-muted">
            <Download className="h-4 w-4" />
            {tr('Export Excel', '匯出 Excel', '导出 Excel')}
          </button>
        </div>
      </div>

      {showExportPanel && (
        <div className="bg-card border rounded-xl p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">{tr('From', '開始日期', '开始日期')}</label>
              <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">{tr('To', '結束日期', '结束日期')}</label>
              <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </div>
            <button onClick={exportExcel} disabled={exporting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
              <Download className="h-4 w-4" />
              {exporting ? tr('Exporting…', '匯出中…', '导出中…') : tr('Export', '匯出', '导出')}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {tr('Exports all bank statement transactions in the date range — Date, Description, Voucher, Deposit, Withdrawal, Balance.',
              '匯出日期範圍內所有銀行月結單交易 — 日期、描述、憑證、存入、支出、結餘。',
              '导出日期范围内所有银行月结单交易 — 日期、描述、凭证、存入、支出、结余。')}
          </p>
        </div>
      )}

      {showManualEntry && (
        <ManualBankStatementEditor
          onSave={async (data) => {
            try {
              const res = await api('/bank-statements/manual', { method: 'POST', body: data }) as any;
              toast.success(tr('Manual statement created — review to post', '手動報表已建立——請審核後入帳', '手动报表已建立——请审核后入账'));
              setShowManualEntry(false);
              navigate(`/bank-statements/review/${res.id}`);
            } catch (e: any) {
              toast.error(e?.message || 'Failed to create manual statement');
            }
          }}
          onCancel={() => setShowManualEntry(false)}
        />
      )}

      {activeFilter === 'unmatched' && (
        <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" />
            <span>{tr('Showing only unreconciled transactions', '僅顯示未對賬交易', '仅显示未对账交易')}</span>
          </div>
          <button
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete('filter');
              navigate(`/bank-statements${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
            }}
            className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 underline"
          >
            {tr('Clear filter', '清除篩選', '清除筛选')}
          </button>
        </div>
      )}

      <PendingReviewBanner />

      {/* Continuity Chain */}
      <div id="statement-continuity-chain">
        <ContinuityChain endpoint="/bank-statements/continuity" queryKey="bank-continuity" type="bank" onSelectStatement={handleChainSelect} />
      </div>

      {/* Statements list */}
      <div className="bg-card border rounded-xl p-6 space-y-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Landmark className="h-4 w-4" /> {t('bank.list')} ({statements.length})
        </h3>
        {isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p> :
         statements.length === 0 ? <p className="text-sm text-muted-foreground">{t('bank.noData')}</p> :
         activeFilter === 'unmatched' && statements.every((s: any) => s.unlinked_count === 0) ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 text-green-900 dark:text-green-200 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {tr('No unreconciled transactions found', '沒有未對賬交易', '没有未对账交易')}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-xs text-primary hover:underline mt-2"
            >
              {tr('Back to dashboard', '返回主頁', '返回主页')}
            </button>
          </div>
         ) : (
          <div className="space-y-2">
            {accountGroups.map((grp: any) => (
              <div key={grp.key} className="rounded-lg border overflow-hidden">
                <button
                  onClick={() => setExpandedAccounts(prev => {
                    const next = new Set(prev);
                    if (next.has(grp.key)) next.delete(grp.key); else next.add(grp.key);
                    return next;
                  })}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-muted/20 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 ${expandedAccounts.has(grp.key) ? '' : '-rotate-90'}`} />
                    <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">{grp.bank_name || 'Statement'}</span>
                    {grp.account_number && <span className="text-xs text-muted-foreground">{grp.account_number}</span>}
                    {grp.account_type && <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{grp.account_type}</span>}
                    {grp.currency && <span className="text-xs font-mono text-muted-foreground">{grp.currency}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {grp.statements.length} {tr('months', '個月', '个月')}
                  </span>
                </button>
                {expandedAccounts.has(grp.key) && (
                  <StatementGroupTable
                    statements={grp.statements}
                    activeFilter={activeFilter}
                    isStaff={isStaff}
                    t={t}
                    setExpandedId={setExpandedId}
                    setEditMode={setEditMode}
                    setEdits={setEdits}
                    setLocalRows={setLocalRows}
                    setSupModal={setSupModal}
                    deleteMut={deleteMut}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Statement detail modal — replaces the old in-line SlideOpen expansion.
          Editing/matching machinery stays in this page; only the container moved. */}
      {expandedId && (
        <div data-testid="stmt-detail-modal" role="dialog" aria-modal="true"
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4 md:p-6"
          onClick={() => setExpandedId(null)}>
          <div className="bg-card border rounded-xl shadow-xl w-full max-w-[1800px] h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-medium font-mono">
                  {modalMeta ? `${modalMeta.statement_year}-${String(modalMeta.statement_month).padStart(2, '0')}` : '…'}
                </span>
                <span className="text-sm font-medium truncate">{modalMeta?.bank_name}</span>
                {modalMeta?.account_number && <span className="text-xs text-muted-foreground">{modalMeta.account_number}</span>}
                {modalMeta?.account_type && <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{modalMeta.account_type}</span>}
                {modalMeta?.currency && <span className="text-xs font-mono text-muted-foreground">{modalMeta.currency}</span>}
              </div>
              <button onClick={() => setExpandedId(null)}
                className="p-1.5 hover:bg-muted rounded shrink-0"
                title={tr('Close', '關閉', '关闭')} aria-label={tr('Close', '關閉', '关闭')}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-3">
                      <div>
                    {detailQuery.isLoading ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        {tr('Loading transactions...', '載入交易中...', '载入交易中...')}
                      </p>
                    ) : filteredTransactions.length === 0 ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                        <FileText className="h-4 w-4" />
                        {tr('No transactions found', '沒有找到交易', '沒有找到交易')}
                      </div>
                    ) : (
                      <div>
                        {/* Summary bar */}
                        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground mb-3 px-1">
                          <div className="flex flex-wrap items-center gap-3">
                            {detail?.period_start && (
                              <span>Period: {detail.period_start} – {detail.period_end}</span>
                            )}
                            <span>Opening: <span className="font-mono font-medium">{detail?.opening_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '-'}</span></span>
                            <span>Closing: <span className="font-mono font-medium text-green-900 dark:text-green-200">{detail?.closing_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '-'}</span></span>
                            {modalPostingSummary && (
                              <span>
                                {tr(
                                  `Posting: ${modalPostingSummary.posted_own} posted · ${modalPostingSummary.posted_matched} by invoice payment · ${modalPostingSummary.no_posting} no posting needed · ${modalPostingSummary.missing} missing`,
                                  `過賬：${modalPostingSummary.posted_own} 已過賬 · ${modalPostingSummary.posted_matched} 已由發票付款 · ${modalPostingSummary.no_posting} 無需過賬 · ${modalPostingSummary.missing} 待補記`,
                                  `过账：${modalPostingSummary.posted_own} 已过账 · ${modalPostingSummary.posted_matched} 已由发票付款 · ${modalPostingSummary.no_posting} 无需过账 · ${modalPostingSummary.missing} 待补记`,
                                )}
                              </span>
                            )}
                            <div className="flex items-center gap-1">
                              {modalPostingSummary && modalPostingState === 'post_missing' && (
                                <button
                                  onClick={() => expandedId && postMissingMut.mutate(expandedId)}
                                  disabled={postMissingMut.isPending}
                                  className="px-2 py-1 text-xs rounded border border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950 flex items-center gap-1 disabled:opacity-50"
                                  title={tr('Create the missing journal entries for this statement', '為此月結單補記缺少的分錄', '为此月结单补记缺少的分录')}
                                >
                                  {postMissingMut.isPending
                                    ? tr('Posting…', '過賬中…', '过账中…')
                                    : tr(`Post missing entries (${modalPostingSummary?.missing || 0})`, `補記缺少的分錄（${modalPostingSummary?.missing || 0}）`, `补记缺少的分录（${modalPostingSummary?.missing || 0}）`)}
                                </button>
                              )}
                              {modalPostingSummary && modalPostingState === 'posted' && (
                                <span className="text-xs font-medium text-green-800 dark:text-green-300">
                                  {(modalPostingSummary?.posted_own || 0) > 0
                                    ? tr('✓ Posted', '✓ 已過賬', '✓ 已过账')
                                    : tr('✓ Recorded by invoice payment', '✓ 已由發票付款記錄', '✓ 已由发票付款记录')}
                                </span>
                              )}
                              {modalPostingSummary && modalPostingState === 'nothing' && (
                                <span className="text-xs text-muted-foreground">
                                  {tr('Nothing to post', '無需過賬', '无需过账')}
                                </span>
                              )}
                              <a href={`/api/bank-statements/${expandedId}/file?token=${localStorage.getItem('token') || ''}${modalMeta?.source_page ? `#page=${modalMeta.source_page}` : ''}`}
                                target="_blank"
                                className="px-2 py-1 text-xs rounded border hover:bg-muted flex items-center gap-1"
                                title={tr('View the original statement PDF', '查看原始月結單 PDF', '查看原始月结单 PDF')}>
                                <Eye className="h-3 w-3" /> {tr('View PDF', '查看 PDF', '查看 PDF')}
                              </a>
                              <button onClick={() => navigate(`/bank-statements/review/${expandedId}`)}
                                className="px-2 py-1 text-xs rounded border hover:bg-muted flex items-center gap-1"
                                title={tr('Review extracted data (PDF + edit view)', '審核提取數據（PDF + 編輯檢視）', '审核提取数据（PDF + 编辑检视）')}>
                                <FileText className="h-3 w-3" /> {tr('Review Extracted Data', '審核提取數據', '审核提取数据')}
                              </button>
                              {!isStaff && (
                              <button
                                onClick={() => exportStatementTransactionsXlsx(
                                  transactions,
                                  modalMeta?.statement_year
                                    ? `${modalMeta.statement_year}-${String(modalMeta.statement_month).padStart(2, '0')}`
                                    : (expandedId || 'statement'),
                                )}
                                className="px-2 py-1 text-xs rounded border hover:bg-muted flex items-center gap-1"
                                title={tr('Export this statement as Excel', '將此月結單匯出為 Excel', '将此月结单导出为 Excel')}>
                                <Download className="h-3 w-3" /> Excel
                              </button>
                              )}
                              <button onClick={() => { setEditMode(!editMode); setEdits({}); setLocalRows([]); }}
                                className={`px-2 py-1 text-xs rounded border ${editMode ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                                {editMode
                                  ? (tr('Done Editing', '完成編輯', '完成編輯'))
                                  : (tr('✏️ Edit', '✏️ 編輯', '✏️ 編輯'))}
                              </button>
                              {editMode && (
                                <button onClick={() => {
                                  const newId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
                                  setLocalRows(prev => [...prev, {
                                    id: newId,
                                    transaction_date: detail?.period_start || '',
                                    description: '',
                                    deposit_amount: 0,
                                    withdrawal_amount: 0,
                                    balance: detail?.opening_balance ?? 0,
                                    account_type: '',
                                    reference: null,
                                    payee_payer: null,
                                    cheque_no: null,
                                    sort_order: filteredTransactions.length + prev.length,
                                    match_status: 'unmatched',
                                  }]);
                                }}
                                  className="px-2 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium"
                                  title={tr('Add a transaction row OCR may have missed', '新增一筆可能被 OCR 遺漏的交易', '新增一笔可能被 OCR 遗漏的交易')}>
                                  {tr('+ Add Row', '+ 新增列', '+ 新增行')}
                                </button>
                              )}
                              {/* TEMPORARILY HIDDEN (2026-10-13): the 🔓 Show COA /
                                  🔒 Reconciled toggle and the 🔍 Review vs Ledger
                                  button. The review engine + reconData panel are
                                  kept wired — review is slated to return as ONE
                                  page-level button checking all bookings. */}
                            </div>
                          </div>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-left text-xs text-muted-foreground">
                                <th className="py-2 pr-3 font-medium">Date</th>
                                <th className="py-2 pr-3 font-medium">Description</th>
                                <th className="py-2 pr-3 font-medium min-w-[110px]">{tr('Payee / Payer', '收款人 / 付款人', '收款人 / 付款人')}</th>
                                <th className="py-2 pr-3 font-medium min-w-[90px]">{tr('Cheque No.', '支票號碼', '支票号码')}</th>
                                <th className="py-2 pr-3 font-medium max-w-[120px]">{tr('Voucher', '憑證', '凭证')}</th>
                                {detail?.accounts?.length > 1 && <th className="py-2 pr-3 font-medium">Account</th>}
                                <th className="py-2 pr-3 font-medium text-right">Deposit</th>
                                <th className="py-2 pr-3 font-medium text-right">Withdrawal</th>
                                <th className="py-2 pr-3 font-medium text-right">Balance</th>
                                <th className="py-2 pr-3 font-medium min-w-[200px]">Account</th>
                                <th className="py-2 pr-1 font-medium w-6" aria-label="Expand"></th>
                                <th className="py-2 font-medium text-center">{tr('Linked Document', '連結文件', '连结文件')}</th>
                                {editMode && <th className="py-2 font-medium text-center w-16">Save</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {[...displayTransactions, ...localRows].map((tx: Transaction) => {
                                const e = edits[tx.id] || {};
                                const date = e.transaction_date !== undefined ? e.transaction_date : tx.transaction_date;
                                const desc = e.description !== undefined ? e.description : tx.description;
                                const payee = e.payee_payer !== undefined ? e.payee_payer : (tx.payee_payer ?? null);
                                const chq = e.cheque_no !== undefined ? e.cheque_no : (tx.cheque_no ?? null);
                                const dep = e.deposit_amount !== undefined ? e.deposit_amount : tx.deposit_amount;
                                const wit = e.withdrawal_amount !== undefined ? e.withdrawal_amount : tx.withdrawal_amount;
                                const bal = e.balance !== undefined ? e.balance : tx.balance;
                                const dirty = !!edits[tx.id];
                                const posting = (tx as any).posting as { entry_id: string; entry_number: string; entry_source: string; lines: { account_code: string; account_name: string; debit: number; credit: number }[] } | null;
                                  const stmtBankCode: string = detail?.account_code || '11103';
                                  const postingView = rowPostingView(tx);
                                  const linkedInvoices = ((tx as any).linked_invoices || []) as NonNullable<Transaction['linked_invoices']>;
                                  const extraLinkedInvoices = linkedInvoices.filter(li => li.invoice_number !== tx.invoice_number);
                                  const hasLinkedDocument = !!tx.invoice_number || !!tx.card_statement_id || linkedInvoices.length > 0;
                                const postingLines: { account_code: string; account_name: string; debit: number; credit: number }[] = postingView.lines;
                                const voucherNo = (tx as any).voucher_number || (tx as any).payment_entry_number;
                                const movement = dep > 0 ? dep : wit;
                                const isLocal = tx.id.startsWith('local-');
                                const effAccountCode = (e.account_code !== undefined ? e.account_code : tx.account_code) || '';
                                const effMatchStatus = (e.match_status !== undefined ? e.match_status : tx.match_status) ?? null;
                                const noCoaFlag = isMissingCoa({ amount: dep > 0 ? dep : -wit, accountCode: effAccountCode, matchStatus: effMatchStatus });

                                return (
                                  <React.Fragment key={tx.id}>
                                <tr id={`tx-row-${tx.id}`} onClick={() => { if (!editMode) setExpandedTxId(expandedTxId === tx.id ? null : tx.id); }}
                                  className={`cursor-pointer border-b border-muted/50 hover:bg-muted/20 ${dirty ? 'bg-blue-50 dark:bg-blue-950/20' : ''} ${
                                  tx.match_status === 'suggested' ? 'bg-yellow-50 dark:bg-yellow-950/20' :
                                  tx.match_status === 'confirmed' ? 'bg-green-50 dark:bg-green-950/20' : ''
                                } ${
                                  activeFilter === 'unmatched' && !tx.invoice_id && !tx.card_statement_id &&
                                  tx.match_status !== 'confirmed' && tx.match_status !== 'skipped' &&
                                  tx.match_status !== 'not_required'
                                    ? 'border-l-4 border-l-amber-400' : ''
                                } ${noCoaFlag && editMode ? 'border-l-4 border-l-amber-400' : ''} ${highlightId === tx.id ? 'bg-yellow-100 dark:bg-yellow-900/30 ring-2 ring-yellow-400' : ''}`}>
                                  <td className="py-1.5 pr-3 whitespace-nowrap">
                                    {/* Flag: unlinked + not skipped → needs attention */}
                                    {!editMode && !tx.invoice_number && !tx.card_statement_id && tx.match_status !== 'suggested' && tx.match_status !== 'skipped' && tx.match_status !== 'not_required' && (
                                      <span className="inline-flex items-center mr-1 text-amber-900 dark:text-amber-100" title="This transaction needs a linked document or to be marked as skipped">
                                        <AlertTriangle className="h-3 w-3" />
                                      </span>
                                    )}
                                    {editMode ? (
                                      <input value={date || ''} onChange={e => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], transaction_date: e.target.value}}))}
                                        className="w-24 px-1 py-0.5 border rounded text-xs bg-background" />
                                    ) : (
                                      <span className="text-muted-foreground">{tx.transaction_date?.slice(5)}</span>
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3 max-w-[300px]">
                                    {editMode ? (
                                      <input value={desc || ''} onChange={e => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], description: e.target.value}}))}
                                        className="w-full px-1 py-0.5 border rounded text-xs bg-background" />
                                    ) : (
                                      <span className="truncate block" title={tx.description}>{tx.description}</span>
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3 max-w-[150px]">
                                    <PayeeCell value={payee} editing={editMode}
                                      placeholder={tr('Payee / payer', '收款人 / 付款人', '收款人 / 付款人')}
                                      onChange={v => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], payee_payer: v}}))} />
                                  </td>
                                  <td className="py-1.5 pr-3">
                                    <PayeeCell value={chq} editing={editMode}
                                      placeholder={tr('Cheque no.', '支票號碼', '支票号码')}
                                      onChange={v => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], cheque_no: v}}))} />
                                  </td>
                                  <td className="py-1.5 pr-3 max-w-[120px]">
                                    {voucherNo ? (
                                      <span className="text-[10px] font-mono bg-muted/30 px-1 py-0.5 rounded truncate block" title={voucherNo}>
                                        {voucherNo}
                                      </span>
                                    ) : (
                                      <span className="text-[10px] text-muted-foreground italic">—</span>
                                    )}
                                  </td>
                                  {detail?.accounts?.length > 1 && (
                                    <td className="py-1.5 pr-3">
                                      <span className="text-xs bg-muted px-1 rounded">{tx.account_type}</span>
                                    </td>
                                  )}
                                  <td className="py-1.5 pr-3 text-right font-mono text-green-900 dark:text-green-200">
                                    {editMode ? (
                                      <input type="number" step="0.01" value={dep || 0} onChange={e => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], deposit_amount: parseFloat(e.target.value) || 0}}))}
                                        className="w-24 px-1 py-0.5 border rounded text-xs text-right bg-background" />
                                    ) : (
                                      dep > 0 ? dep.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3 text-right font-mono text-red-900 dark:text-red-200">
                                    {editMode ? (
                                      <input type="number" step="0.01" value={wit || 0} onChange={e => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], withdrawal_amount: parseFloat(e.target.value) || 0}}))}
                                        className="w-24 px-1 py-0.5 border rounded text-xs text-right bg-background" />
                                    ) : (
                                      wit > 0 ? wit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3 text-right font-mono">
                                    {editMode ? (
                                      <input type="number" step="0.01" value={bal != null ? bal : 0} onChange={e => setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], balance: parseFloat(e.target.value) || 0}}))}
                                        className="w-24 px-1 py-0.5 border rounded text-xs text-right bg-background" />
                                    ) : (
                                      bal > 0 ? bal.toLocaleString(undefined, { minimumFractionDigits: 2 }) :
                                      bal < 0 ? <span className="text-red-900 dark:text-red-200">{bal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> :
                                      '0.00'
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3" onClick={e => e.stopPropagation()}>
                                    {postingLines.length > 0 ? (
                                      <div className="flex flex-col items-start gap-0.5">
                                        {postingLines.map((l, i) => {
                                          const side = l.debit > 0 ? 'Dr' : 'Cr';
                                          const amount = l.debit > 0 ? Number(l.debit) : Number(l.credit);
                                          const accName = accounts.find((a: any) => a.account_code === l.account_code)?.account_name || l.account_name || '';
                                          return (
                                            <label key={`${l.account_code}-${i}`} className="inline-flex items-center gap-1 max-w-[380px] text-[11px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                                              <span className={`text-[10px] font-semibold shrink-0 ${side === 'Dr' ? 'text-blue-600 dark:text-blue-400' : 'text-orange-900 dark:text-orange-200'}`}>{side}</span>
                                              <select
                                                value={l.account_code}
                                                disabled={!postingView.editable || (hideReconciledCoa && detail?.is_reconciled)}
                                                onChange={(e) => {
                                                  if (e.target.value === NA_ACCOUNT) {
                                                    const ok = window.confirm(tr(
                                                      'Mark this whole transaction as Not applicable? This will remove its posting.',
                                                      '將整筆交易標為不適用？這會移除其分錄。',
                                                      '将整笔交易标为不适用？这会移除其分录。',
                                                    ));
                                                    if (!ok) {
                                                      e.target.value = l.account_code;
                                                      return;
                                                    }
                                                    updateTxMut.mutate({ id: tx.id, body: { account_code: null, match_status: 'not_required' } });
                                                    return;
                                                  }
                                                  if (postingView.editable) setExpandedTxId(expandedTxId === tx.id ? null : tx.id);
                                                }}
                                                title={`${side} ${l.account_code} · ${accName} · ${amount.toFixed(2)}`}
                                                className="bg-transparent border-0 p-0 m-0 text-[11px] font-mono outline-none max-w-[320px] truncate">
                                                {postingView.editable ? (
                                                  <>
                                                    <option value={NA_ACCOUNT}>{tr('Not applicable', '不適用', '不适用')}</option>
                                                    {coaOptions}
                                                  </>
                                                ) : (
                                                <option value={l.account_code}>
                                                  {l.account_code} {accName} · {amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </option>
                                                )}
                                              </select>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    ) : postingLines.length > 0 ? (
                                      <div className="flex flex-col items-start gap-0.5">
                                        {postingLines.map((l, i) => {
                                          const side = l.debit > 0 ? 'Dr' : 'Cr';
                                          const accName = accounts.find((a: any) => a.account_code === l.account_code)?.account_name || l.account_name || '';
                                          const clickable = !(hideReconciledCoa && detail?.is_reconciled);
                                          const actionHint = postingView.source === 'payment'
                                            ? tr(' (click to view posting)', '（點擊查看分錄）', '（点击查看分录）')
                                            : tr(' (click to edit Dr/Cr posting)', '（點擊編輯借貸分錄）', '（点击编辑借贷分录）');
                                          return (
                                            <span key={`${l.account_code}-${i}`}
                                              className={`inline-flex items-center gap-1 max-w-[260px] text-[10px] font-mono px-1 py-px rounded ${
                                                isTemporaryAccount(accName)
                                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                  : 'bg-primary/10 text-primary'
                                              } ${clickable ? 'cursor-pointer hover:bg-primary/20' : ''}`}
                                              title={`${side} ${l.account_code} · ${accName} · ${(l.debit > 0 ? l.debit : l.credit).toFixed(2)}${clickable ? tr(' (click to edit Dr/Cr posting)', '（點擊編輯借貸分錄）', '（点击编辑借贷分录）') : ''}`}
                                              onClick={() => { if (clickable) setExpandedTxId(expandedTxId === tx.id ? null : tx.id); }}>
                                              <span className={`text-[9px] font-semibold ${side === 'Dr' ? 'text-blue-600 dark:text-blue-400' : 'text-orange-900 dark:text-orange-200'}`}>{side}</span>
                                              <span className="truncate">{l.account_code}<span className="text-muted-foreground ml-1">{accName}</span></span>
                                            </span>
                                          );
                                        })}
                                        {postingLines.length > 2 && (
                                          <span className="text-[9px] text-muted-foreground">
                                            {(tx as any).posting?.entry_source === 'manual' ? tr('manual split', '手動拆分', '手动拆分') : tr('split', '拆分', '拆分')}
                                          </span>
                                        )}
                                      </div>
                                    ) : tx.account_code && (tx.invoice_id || tx.payment_entry_number || tx.match_status === 'confirmed' || detail?.is_reconciled) ? (
                                      (() => {
                                        const acc = accounts.find((a: any) => a.account_code === tx.account_code);
                                        const name = acc?.account_name || '(unknown account)';
                                        const isReconciled = !!detail?.is_reconciled;
                                        return (
                                          <span
                                            className={`text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded inline-block max-w-[260px] truncate ${
                                              isReconciled && hideReconciledCoa ? 'opacity-60 cursor-default' : 'cursor-pointer hover:bg-primary/20'
                                            }`}
                                            title={`${tx.account_code} · ${name}${isReconciled ? tr(' (reconciled)', '（已對賬）', '（已对账）') : ''}`}
                                            onClick={() => { if (!(isReconciled && hideReconciledCoa)) setAcctModalTx(tx); }}>
                                            <span className="font-mono">{tx.account_code}</span>
                                            <span className="text-muted-foreground ml-1">{name}</span>
                                            {isReconciled && <span className="text-[9px] ml-1 text-green-900 dark:text-green-200">✓</span>}
                                          </span>
                                        );
                                      })()
                                    ) : tx.match_status === 'not_required' && naEditTxId !== tx.id ? (
                                      <button
                                        onClick={() => setNaEditTxId(tx.id)}
                                        className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded inline-flex items-center gap-1 hover:bg-muted/70 hover:text-foreground"
                                        title={tr('Not applicable — click to change the posting', '不適用——點擊可更改記賬', '不适用——点击可更改记账')}>
                                        <Ban className="h-3 w-3" /> {tr('Not applicable', '不適用', '不适用')}
                                      </button>
                                    ) : hideReconciledCoa && detail?.is_reconciled ? (
                                      <span className="text-xs text-muted-foreground italic">—</span>
                                    ) : (
                                      /* Unified unposted-state picker: pill-styled Dr/Cr dropdowns matching
                                         the posted rows' pills above. Deposits pre-fill Dr with the bank
                                         account, withdrawals Cr. Saving fires when both sides are chosen. */
                                      (() => {
                                        const isDep = dep > 0;
                                        // Pre-fill the bank side only when that code exists in this COA
                                        const bankDefault = accounts.some((a: any) => a.account_code === stmtBankCode) ? stmtBankCode : '';
                                        const contraDefault = isLocal ? (effAccountCode || '') : (tx.account_code || '');
                                        const sides = naSides[tx.id] || {
                                          dr: isDep ? bankDefault : contraDefault,
                                          cr: isDep ? contraDefault : bankDefault,
                                        };
                                        const setSide = (side: 'dr' | 'cr', v: string) => {
                                          if (v === NA_ACCOUNT) {
                                            setNaEditTxId(null);
                                            setNaSides(prev => { const next = { ...prev }; delete next[tx.id]; return next; });
                                            if (isLocal) {
                                              setEdits(prev => ({ ...prev, [tx.id]: { ...prev[tx.id], account_code: null, match_status: 'not_required' } }));
                                            } else {
                                              updateTxMut.mutate({ id: tx.id, body: { account_code: null, match_status: 'not_required' } });
                                            }
                                            return;
                                          }
                                          const next = { ...sides, [side]: v };
                                          setNaSides(prev => ({ ...prev, [tx.id]: next }));
                                          const contra = isDep ? next.cr : next.dr;
                                          const bankSide = isDep ? next.dr : next.cr;
                                          if ((contra && bankSide) || !contra) {
                                            saveNaSides(tx, next, movement, stmtBankCode);
                                          }
                                        };
                                        const pillSelect = (side: 'dr' | 'cr', label: 'Dr' | 'Cr', labelCls: string) => {
                                          const picked = !!sides[side];
                                          return (
                                            <label className={`inline-flex items-center gap-1 max-w-[380px] text-[11px] font-mono px-1.5 py-0.5 rounded cursor-pointer ${
                                              picked ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                                            }`}>
                                              <span className={`text-[10px] font-semibold shrink-0 ${labelCls}`}>{label}</span>
                                              <select value={sides[side]} onChange={e => setSide(side, e.target.value)}
                                                onClick={e => e.stopPropagation()}
                                                className={`bg-transparent border-0 p-0 m-0 text-[11px] font-mono outline-none cursor-pointer max-w-[320px] truncate ${
                                                  picked ? 'text-primary' : 'text-muted-foreground'
                                                }`}>
                                                <option value="">{tr('— pick —', '— 選 —', '— 选 —')}</option>
                                                <option value={NA_ACCOUNT}>{tr('Not applicable', '不適用', '不适用')}</option>
                                                {coaOptions}
                                              </select>
                                            </label>
                                          );
                                        };
                                        return (
                                          <div className="flex flex-col items-start gap-0.5" onClick={e => e.stopPropagation()}
                                            title={tr('Pick the Dr and Cr accounts to post this row (leave empty = Not-Applicable)', '選擇借貸科目以為此行記賬（留空＝不適用）', '选择借贷科目以为此行记账（留空＝不适用）')}>
                                            {pillSelect('dr', 'Dr', 'text-blue-600 dark:text-blue-400')}
                                            {pillSelect('cr', 'Cr', 'text-orange-900 dark:text-orange-200')}
                                          </div>
                                        );
                                      })()
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-1 w-6 text-muted-foreground">
                                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${expandedTxId === tx.id ? 'rotate-180' : ''}`} />
                                  </td>
                                  <td className="py-1.5 text-center" onClick={e => e.stopPropagation()}>
                                    {/* ── Invoice linked ── */}
                                      {tx.invoice_number && (
                                        <span
                                          data-testid="invoice-reference-badge"
                                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${tx.match_status === 'suggested' ? 'text-yellow-900 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900/30' : 'text-green-700 bg-green-100 dark:bg-green-900/30'}`}
                                        >
                                          {tx.invoice_number}
                                        {tx.match_status === 'suggested' ? (
                                          <>
                                            <button onClick={() => confirmMatchMut.mutate({ txId: tx.id, invoiceId: tx.invoice_id! })}
                                              className="p-0.5 text-green-900 dark:text-green-200 hover:bg-green-100 rounded" title="Confirm">
                                              <Check className="h-3.5 w-3.5" />
                                            </button>
                                            <button onClick={() => unlinkMatchWithReason(tx.id)}
                                              className="p-0.5 text-red-900 dark:text-red-200 hover:bg-red-100 rounded" title="Reject">
                                              <X className="h-3.5 w-3.5" />
                                            </button>
                                          </>
                                        ) : (
                                          <button onClick={() => unlinkMatchWithReason(tx.id)} className="hover:text-red-900 dark:text-red-200" title="Unlink">
                                            <X className="h-3 w-3" />
                                          </button>
                                        )}
                                        </span>
                                      )}
                                      {extraLinkedInvoices.slice(0, 2).map(li => (
                                        <span
                                          key={li.invoice_id || li.invoice_number}
                                          data-testid="invoice-reference-badge"
                                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded text-green-700 bg-green-100 dark:bg-green-900/30"
                                        >
                                          <span className="font-medium">{li.invoice_number}</span>
                                          {li.allocated_amount != null && (
                                            <span className="text-muted-foreground font-mono">
                                              {li.allocated_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </span>
                                          )}
                                        </span>
                                      ))}
                                      {linkedInvoices.length > 2 && (
                                        <span
                                          className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-medium text-muted-foreground"
                                          title={linkedInvoices.map(li => li.invoice_number).join(', ')}
                                        >
                                          +{linkedInvoices.length - 2}
                                        </span>
                                      )}
                                      {/* ── Card statement linked ── */}
                                    {tx.card_statement_id && tx.card_issuer && (
                                      <span className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 rounded">
                                        <CreditCard className="h-3 w-3" />
                                        {tx.card_issuer}
                                        {tx.cs_statement_year && ` ${tx.cs_statement_year}-${String(tx.cs_statement_month || 1).padStart(2, '0')}`}
                                        <button onClick={() => cardLinkMut.mutate({ txId: tx.id, action: 'unlink' })} className="hover:text-red-900 dark:text-red-200" title="Unlink card">
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    )}
                                    {/* ── Opted out (no link needed) ── */}
                                    {!tx.card_statement_id && !tx.invoice_number && tx.match_status === 'skipped' && (
                                      <button onClick={() => skipLinkMut.mutate(tx.id)}
                                        className="text-gray-900 dark:text-gray-200 hover:text-amber-900 dark:text-amber-100" title="Undo — mark as needing a link">
                                        <Ban className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                      {/* ── Opening balance — no link required ── */}
                                    {!tx.card_statement_id && !tx.invoice_number && tx.match_status === 'not_required' && (
                                      <span
                                        className="inline-flex items-center gap-1 text-muted-foreground"
                                        title={tr('Opening balance — no link or posting required', '期初結餘——無需連結或過賬', '期初结余——无需连结或过账')}
                                      >
                                        <span className="px-1 py-px rounded bg-muted border border-border font-medium text-[10px]">N/A</span>
                                        </span>
                                      )}
                                      {!hasLinkedDocument && tx.match_status !== 'skipped' && tx.match_status !== 'not_required' && (
                                        <span className="text-muted-foreground" aria-label={tr('No linked invoice', '沒有連結發票', '没有连结发票')}>—</span>
                                      )}
                                      {/* ── Two-icon row: link or opt-out ── */}
                                    {!tx.card_statement_id && !tx.invoice_number && tx.match_status !== 'suggested' && tx.match_status !== 'skipped' && tx.match_status !== 'not_required' && (
                                      <div className="flex items-center gap-1.5 justify-center">
                                        <button onClick={() => setMatchTxId(tx.id)}
                                          className="text-muted-foreground hover:text-primary" title="Link to invoice or card statement">
                                          <Link2 className="h-3.5 w-3.5" />
                                        </button>
                                        <button onClick={() => skipLinkMut.mutate(tx.id)}
                                          className="text-muted-foreground hover:text-gray-900 dark:text-gray-200" title="No link needed — opt out">
                                          <Ban className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                  {editMode && (
                                    <td className="py-1.5 text-center">
                                      <div className="flex items-center gap-1 justify-center">
                                        <button onClick={async () => {
                                          if (aiLoading) return;
                                          setAiLoading(tx.id);
                                          try {
                                            const data = await api('/chat', {
                                              method: 'POST',
                                              body: {
                                                message: `Fix this bank transaction if it looks wrong. Common errors: description merged from multiple lines, amounts that don't match the merchant (e.g., NAME-CHEAP is ~$100 not $14,000).

Date: ${tx.transaction_date}
Description: ${tx.description}
Deposit: ${tx.deposit_amount}
Withdrawal: ${tx.withdrawal_amount}
Balance: ${tx.balance}

Return ONLY a JSON object with corrected fields. If nothing needs fixing, return {}. Format: {"description":"...","deposit_amount":N,"withdrawal_amount":N,"note":"explanation"}`,
                                                history: [],
                                              },
                                            });
                                            const reply = data.reply || '';
                                            // Extract JSON from reply (skip DSML tags)
                                            const cleanReply = reply.replace(/<[^>]+>/g, '');
                                            const jsonMatch = cleanReply.match(/\{[\s\S]*\}/);
                                            if (jsonMatch) {
                                              try {
                                                const json = JSON.parse(jsonMatch[0]);
                                                if (json.description || json.deposit_amount !== undefined || json.withdrawal_amount !== undefined) {
                                                  const update: any = {};
                                                  if (json.description) update.description = json.description;
                                                  if (json.deposit_amount !== undefined) update.deposit_amount = json.deposit_amount;
                                                  if (json.withdrawal_amount !== undefined) update.withdrawal_amount = json.withdrawal_amount;
                                                  if (json.balance !== undefined) update.balance = json.balance;
                                                  setEdits(prev => ({...prev, [tx.id]: {...prev[tx.id], ...update}}));
                                                  if (json.note) toast.info('AI: ' + json.note);
                                                } else {
                                                  toast.info(tr('AI determined no changes needed', 'AI 認為此交易無需修改', 'AI 认为此交易无需修改'));
                                                }
                                              } catch { toast.info(tr('AI response could not be parsed: ', 'AI 回應無法解析：', 'AI 回应无法解析：') + cleanReply.slice(0, 200)); }
                                            } else {
                                              toast.info(tr('AI response: ', 'AI 回應：', 'AI 回应：') + reply.slice(0, 300));
                                            }
                                          } catch (e: any) { toast.info('AI 失敗：' + (e.message || 'unknown')); }
                                          setAiLoading(null);
                                        }}
                                          disabled={aiLoading === tx.id}
                                          className={`px-2 py-0.5 text-xs rounded text-white ${
                                            aiLoading === tx.id
                                              ? 'bg-purple-300 animate-pulse'
                                              : 'bg-purple-500 hover:opacity-90'
                                          }`}
                                          title="AI 根據 OCR 原始資料修正">
                                          {aiLoading === tx.id ? '⏳' : '🤖'} AI
                                        </button>
                                        <button onClick={async () => {
                                          if (isLocal) {
                                            const combined = { ...tx, ...(edits[tx.id] || {}) };
                                            try {
                                              await createTxMut.mutateAsync({
                                                transaction_date: combined.transaction_date || null,
                                                description: combined.description,
                                                deposit_amount: combined.deposit_amount || 0,
                                                withdrawal_amount: combined.withdrawal_amount || 0,
                                                balance: combined.balance ?? null,
                                                account_code: combined.account_code ?? null,
                                                payee_payer: combined.payee_payer ?? null,
                                                cheque_no: combined.cheque_no ?? null,
                                                match_status: combined.match_status ?? 'unmatched',
                                              });
                                              setLocalRows(prev => prev.filter(r => r.id !== tx.id));
                                              setEdits(prev => { const n = { ...prev }; delete n[tx.id]; return n; });
                                            } catch (e: any) {
                                              toast.error(`Failed to create row: ${e?.error || e?.message || 'Unknown error'}`);
                                            }
                                          } else {
                                            updateTxMut.mutate({ id: tx.id, body: edits[tx.id] });
                                            setEdits(prev => { const n = {...prev}; delete n[tx.id]; return n; });
                                          }
                                        }}
                                          disabled={!dirty && !isLocal}
                                          className="px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-30">
                                          {isLocal ? 'Create' : 'Save'}
                                        </button>
                                        {isLocal && (
                                          <button onClick={() => {
                                            setLocalRows(prev => prev.filter(r => r.id !== tx.id));
                                            setEdits(prev => { const n = { ...prev }; delete n[tx.id]; return n; });
                                          }}
                                            className="px-1.5 py-0.5 text-xs text-red-900 dark:text-red-200 hover:bg-red-50 rounded"
                                            title={tr('Remove row', '移除列', '移除行')}>
                                            ✕
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                {!editMode && (
                                  <tr>
                                    <td colSpan={detail?.accounts?.length > 1 ? 12 : 11} className={`p-0 ${expandedTxId === tx.id ? 'border-b border-muted/50' : ''}`}>
                                      <SlideOpen open={expandedTxId === tx.id}>
                                        {!!(tx as any).linked_invoices?.length && (
                                          <div className="bg-blue-50/60 border-b border-blue-100 px-4 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="settles-strip">
                                            <span className="text-muted-foreground">{tr('Settles', '結算', '结算')}:</span>
                                            {(tx as any).linked_invoices.map((li: any) => (
                                              <span key={li.invoice_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border">
                                                <span className="font-medium">{li.invoice_number}</span>
                                                {li.allocated_amount != null && (
                                                  <span className="text-muted-foreground font-mono">{li.allocated_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                )}
                                              </span>
                                            ))}
                                            {(tx as any).payment_entry_number && (
                                              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted">{(tx as any).payment_entry_number}</span>
                                            )}
                                          </div>
                                        )}
                                        <div className="px-4 py-1.5">
                                          <button data-testid="audit-trail-btn" onClick={() => setAuditTx({
                                            statementName: (detail as any)?.file_name || null,
                                            transactionDate: tx.transaction_date,
                                            description: tx.description,
                                            amount: dep > 0 ? dep : wit,
                                            matchStatus: tx.match_status || 'unmatched',
                                            invoiceIds: ((tx as any).linked_invoices || []).map((li: any) => li.invoice_id),
                                            txId: tx.id,
                                            isDeposit: dep > 0,
                                          })} className="text-xs flex items-center gap-1 text-primary hover:underline">
                                            <Link2 className="h-3.5 w-3.5" /> {tr('View audit trail', '查看審計追蹤', '查看审计追踪')}
                                          </button>
                                        </div>
                                        {postingView.source === 'payment' ? (
                                          <div data-testid="payment-posting-lines" className="px-4 py-2">
                                            <div className="text-[11px] font-medium text-muted-foreground mb-1">
                                              {tr('Posted by invoice payment', '由發票付款過賬', '由发票付款过账')}
                                              {(tx as any).payment_posting?.entry_number ? ` · ${(tx as any).payment_posting.entry_number}` : ''}
                                            </div>
                                            {postingLines.map((l, i) => {
                                              const side = l.debit > 0 ? 'Dr' : 'Cr';
                                              const amount = l.debit > 0 ? Number(l.debit) : Number(l.credit);
                                              const name = accounts.find((a: any) => a.account_code === l.account_code)?.account_name || l.account_name || '';
                                              return (
                                                <div key={`${l.account_code}-${i}`} className="flex items-center gap-2 text-xs py-0.5">
                                                  <span className={`text-[9px] font-semibold w-5 ${side === 'Dr' ? 'text-blue-600 dark:text-blue-400' : 'text-orange-900 dark:text-orange-200'}`}>{side}</span>
                                                  <span className="font-mono">{l.account_code}</span>
                                                  <span className="flex-1 truncate text-muted-foreground">{name}</span>
                                                  <span className="font-mono">{amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                        <TxPostingPanel
                                          key={(tx as any).posting?.entry_id || 'auto'}
                                          kind="bank"
                                          movementAmount={movement}
                                          contraSide={dep > 0 ? 'Cr' : 'Dr'}
                                          fixedCode={stmtBankCode}
                                          fixedName={accounts.find((a: any) => a.account_code === stmtBankCode)?.account_name || 'Bank account'}
                                          posting={posting}
                                          currentCode={tx.account_code}
                                          initialContraLines={prefillTxId === tx.id ? prefilledLines : undefined}
                                          accounts={accounts}
                                          tree={coaTree}
                                          disabled={!!detail?.is_reconciled}
                                          lockedReason={tr('Statement is reconciled — reopen reconciliation before changing postings', '月結單已對賬——請先重開對賬再修改分錄', '月结单已对账——请先重开对账再修改分录')}
                                          onSave={(lines) => saveTxPosting(tx.id, movement, lines)}
                                          onResetAuto={() => saveTxPosting(tx.id, movement, undefined, true)}
                                          onMarkNotApplicable={tx.match_status !== 'not_required'
                                            ? () => updateTxMut.mutateAsync({ id: tx.id, body: { account_code: null, match_status: 'not_required' } }).then(() => {})
                                            : undefined}
                                        />
                                        )}
                                      </SlideOpen>
                                    </td>
                                  </tr>
                                )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="border-t font-medium text-xs">
                                <td colSpan={detail?.accounts?.length > 1 ? 5 : 4} className="py-2 text-muted-foreground">
                                  {filteredTransactions.length} transactions
                                  {suggestedCount > 0 && <span className="ml-2 text-yellow-900 dark:text-yellow-200">({suggestedCount} suggested)</span>}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-green-900 dark:text-green-200">
                                  {totalDeposits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-red-900 dark:text-red-200">
                                  {totalWithdrawals.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
            </div>
          </div>
        </div>
      )}

      {/* Account categorization modal */}
      {acctModalTx && (
        <AccountModal
          tx={acctModalTx}
          allTx={filteredTransactions}
          accounts={accounts}
          tree={coaTree}
          onClose={() => setAcctModalTx(null)}
          onApply={(code, _applySimilar, similarIds) => {
            // "Not-Applicable" clears the account and marks the row non-posting
            const isNa = code === NA_ACCOUNT;
            const body = isNa ? { account_code: null, match_status: 'not_required' } : { account_code: code };
            // Update this transaction
            updateTxMut.mutate({ id: acctModalTx.id, body });
            // Update selected similar transactions
            if (similarIds && similarIds.size > 0) {
              similarIds.forEach((tid: string) => {
                updateTxMut.mutate({ id: tid, body });
              });
            }
            setAcctModalTx(null);
          }}
        />
      )}

      {/* Unified link document modal */}
      {matchTxId && (
        <LinkedDocModal
          txId={matchTxId}
          onClose={() => setMatchTxId(null)}
          onLinkInvoices={(ids, variance) => {
            // 1 selection = plain single link; 2+ = combined payment (group confirm)
            if (ids.length === 1) {
              confirmMatchMut.mutate({
                txId: matchTxId, invoiceId: ids[0],
                varianceAmount: variance?.amount, reviewReason: variance?.reason,
              });
            } else {
              confirmMatchMut.mutate({
                txId: matchTxId, invoiceId: null, invoiceIds: ids,
                varianceAmount: variance?.amount, reviewReason: variance?.reason,
              });
            }
            setMatchTxId(null);
          }}
          onLinkCard={(csId) => {
            cardLinkMut.mutate({ txId: matchTxId, csId, action: 'link' });
            setMatchTxId(null);
          }}
        />
      )}

      {/* Auto-Match Review Modal (shared unified component) */}
      {autoMatchResults && (
        <AutoMatchReviewModal
          matches={autoMatchResults}
          onConfirm={(txId, invoiceId, invoiceIds) =>
            confirmMatchMut.mutateAsync({ txId, invoiceId, invoiceIds })
          }
          onReject={(txId) =>
            unlinkMatchAsyncWithReason(txId)
          }
          onClose={() => {
            setAutoMatchResults(null);
            queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
            runPendingCards();
          }}
          useAI={useAI}
          onToggleAI={setUseAI}
        />
      )}

      {/* Card Match Review Modal */}
      {cardMatchResults && (
        <CardMatchReviewModal
          matches={cardMatchResults}
          onConfirm={(txId, csId) => {
            cardLinkMut.mutate({ txId, csId, action: 'link' });
          }}
          onReject={(txId) => {
            skipLinkMut.mutate(txId);
          }}
          onClose={() => {
            setCardMatchResults(null);
            queryClient.invalidateQueries({ queryKey: ['bank-statement', expandedId] });
          }}
        />
      )}

      {/* Review vs Ledger Modal */}
      {reconData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setReconData(null)}>
          <div className="bg-card border rounded-xl p-6 w-full max-w-lg mx-4 space-y-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">{tr('Review vs Ledger', '對帳審查', '对账审查')}</h3>
              {reconData.is_locked && (
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">
                  {tr('Statement is reconciled — read-only', '月結單已對賬——唯讀', '月结单已对账——只读')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-muted/50 rounded-lg p-3">
                <span className="text-muted-foreground text-xs">{tr('Bank', '銀行', '银行')}</span>
                <p className="font-bold text-lg">HKD {(reconData.balance_summary?.statement_balance ?? 0).toLocaleString()}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <span className="text-muted-foreground text-xs">{tr('Books', '帳面', '账面')}</span>
                <p className="font-bold text-lg">HKD {(reconData.balance_summary?.gl_balance ?? 0).toLocaleString()}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">{tr('Gap', '差額', '差额')}</span>
                <p className={`font-bold text-lg ${(Math.abs(reconData.balance_summary?.difference ?? 0) < 0.01) ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
                  HKD {(reconData.balance_summary?.difference ?? 0).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">{tr('After suggestions', '建議後', '建议后')}</span>
                <p className={`font-bold text-lg ${(Math.abs(reconData.projected_difference ?? 0) < 0.01) ? 'text-green-900 dark:text-green-200' : 'text-amber-900 dark:text-amber-100'}`}>
                  HKD {(reconData.projected_difference ?? 0).toLocaleString()}
                </p>
              </div>
            </div>

            {(['adjusting_je', 'invoice_match', 'coa_posting'] as const).map(kind => {
              const group = (reconData.items || []).filter((i: any) => i.kind === kind);
              if (!group.length) return null;
              const titles: Record<string, [string, string, string]> = {
                adjusting_je: ['Suggested adjusting entries', '建議調整分錄', '建议调整分录'],
                invoice_match: ['Suggested invoice matches', '建議發票配對', '建议发票配对'],
                coa_posting: ['Postings needed', '待入帳項目', '待入账项目'],
              };
              const t = titles[kind];
              return (
                <div key={kind}>
                  <p className="text-sm font-semibold mb-1">{tr(t[0], t[1], t[2])}</p>
                  <ul className="space-y-1">
                    {group.map((it: any) => (
                      <li key={it.id} data-testid="review-item" className="flex items-center justify-between gap-2 text-sm border rounded px-2 py-1">
                        <span className="flex-1 min-w-0">
                          <span className={`mr-1 px-1 rounded text-[10px] ${it.source === 'ai' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                            {it.source === 'ai' ? tr('AI', 'AI', 'AI') : tr('RULE', '規則', '规则')}
                          </span>
                          {it.explanation}
                        </span>
                        {!reconData.is_locked && it.transaction_id && (
                          <PreFillButton item={it} bankCode={detail?.account_code || '11101'}
                            onPrefillJe={prefillJe} onPrefillMatch={prefillMatch} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {(reconData.items || []).filter((i: any) => i.kind === 'info').map((it: any) => (
              <p key={it.id} className="text-xs text-muted-foreground">{it.explanation}</p>
            ))}

            <div className="flex gap-3 justify-end">
              <button onClick={() => setReconData(null)} className="px-4 py-2 border rounded-md text-sm">
                {tr('Close', '關閉', '关闭')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Modal */}
      <AuditTrailModal open={!!auditTx} onClose={() => setAuditTx(null)} txContext={auditTx} />

      {supModal?.show && (
        <SupervisorPasswordModal
          action="delete this bank statement"
          onConfirm={supModal.onConfirm}
          onCancel={() => setSupModal(null)}
        />
      )}
    </div>
  );
}


function AccountModal({ tx, allTx, accounts, tree, onClose, onApply }: {
  tx: Transaction;
  allTx: Transaction[];
  accounts: any[];
  tree: CoaNode[];
  onClose: () => void;
  onApply: (code: string, applySimilar: boolean, similarIds?: Set<string>) => void;
}) {
  const { i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedCode, setSelectedCode] = useState(tx.account_code || '');
  const [selectedSimilar, setSelectedSimilar] = useState<Set<string>>(new Set());

  const filtered: CoaNode[] = tree.filter(n => {
    if (!search) return true;
    const q = search.toLowerCase();
    return n.account.account_code.includes(q) || (n.account.account_name || '').toLowerCase().includes(q);
  });

  // Find similar transactions
  const desc = tx.description || '';
  const words = desc.split(/\s+/).filter((w: string) => w.length > 2).slice(0, 3);
  const similar = allTx.filter((t: Transaction) =>
    t.id !== tx.id && words.some((w: string) => (t.description || '').includes(w))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl p-4 w-[80vw] mx-4 h-[70vh] flex flex-col gap-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between shrink-0">
          <h3 className="font-bold text-lg flex items-center gap-2"><Tag className="h-5 w-5" /> {tr('Select Account Code', '選擇會計科目', '选择会计科目')}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
        </div>

        {/* Transaction info */}
        <div className="bg-muted/30 rounded-lg p-3 text-sm flex items-center gap-3 shrink-0">
          <span className="font-medium flex-shrink-0">{tx.transaction_date}</span>
          <span className="text-muted-foreground truncate flex-1 min-w-0">{desc}</span>
          <span className={`font-mono flex-shrink-0 font-medium ${tx.deposit_amount > 0 ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
            {tx.deposit_amount > 0 ? `+${tx.deposit_amount.toLocaleString()}` :
             tx.withdrawal_amount > 0 ? `-${tx.withdrawal_amount.toLocaleString()}` : ''}
          </span>
          {tx.account_code && (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded flex-shrink-0">
              <span className="font-mono">{tx.account_code}</span>
              <span className="ml-1">{accounts.find((a: any) => a.account_code === tx.account_code)?.account_name?.slice(0, 20) || ''}</span>
            </span>
          )}
        </div>

        {/* Search */}
        <div className="relative shrink-0">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tr('Search by code or name...', '輸入科目編號或名稱搜尋...', '輸入科目编号或名称搜索...')}
            className="w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-background" autoFocus />
        </div>

        {/* Account list — hierarchical: parents are non-selectable group headers */}
        <div className="border rounded-lg flex-1 min-h-0 overflow-y-auto">
          <button
            onClick={() => setSelectedCode(NA_ACCOUNT)}
            className={`w-full text-left pr-3 py-2 text-sm hover:bg-muted flex items-center justify-between ${
              selectedCode === NA_ACCOUNT ? 'bg-muted/40 text-muted-foreground font-medium' : ''
            }`}
            title={tr('No COA account / posting required for this row', '此列不需會計科目/記賬', '此行不需会计科目/记账')}
          >
            <span className="ml-3">{tr('Not applicable', '不適用', '不适用')}</span>
            {selectedCode === NA_ACCOUNT && <Check className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          </button>
          {filtered.slice(0, 300).map(n => {
            const a = n.account;
            const selected = selectedCode === a.account_code;
            if (n.isParent) {
              return (
                <div
                  key={a.account_code}
                  title={tr('Category group — select one of its sub-accounts', '類別組——請選擇其下的子科目', '类别组——请选择其下的子科目')}
                  style={{ paddingLeft: `${12 + n.depth * 18}px` }}
                  className="pr-3 py-2 text-sm flex items-center justify-between bg-muted/60 text-muted-foreground border-b border-muted/40 last:border-0"
                >
                  <span className="font-mono text-xs">{a.account_code}</span>
                  <span className="flex-1 ml-3 truncate">{a.account_name}</span>
                  <span className="text-[10px] uppercase tracking-wide opacity-70">{tr('group', '組', '组')}</span>
                </div>
              );
            }
            return (
              <button
                key={a.account_code}
                onClick={() => setSelectedCode(a.account_code)}
                style={{ paddingLeft: `${12 + n.depth * 18}px` }}
                className={`w-full text-left pr-3 py-2 text-sm hover:bg-muted flex items-center justify-between ${
                  selected ? 'bg-primary/10 text-primary font-medium' : ''
                }`}
              >
                <span className="font-mono text-xs">{a.account_code}</span>
                <span className="flex-1 ml-3 truncate">{a.account_name}</span>
                {selected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">{tr('No matching accounts', '無匹配科目', '无匹配科目')}</p>
          )}
        </div>

        {/* Apply to similar transactions */}
        {similar.length > 0 && (
          <div className="border rounded-lg shrink-0">
            <div className="px-3 py-2 bg-muted/30 border-b text-sm font-medium flex items-center gap-2">
              <span>{tr(`Similar transactions (${similar.length})`, `相似交易 (${similar.length})`, `相似交易 (${similar.length})`)}</span>
              <span className="text-xs text-muted-foreground">
                {(() => {
                  const cats = new Map<string, number>();
                  similar.forEach((t: Transaction) => {
                    const k = t.account_code ? `${t.account_code} ${accounts.find((a: any) => a.account_code === t.account_code)?.account_name?.slice(0, 8) || ''}` : '未分類';
                    cats.set(k, (cats.get(k) || 0) + 1);
                  });
                  return Array.from(cats.entries()).map(([k, v]) => `${k}(${v})`).join('  ');
                })()}
              </span>
              <span className="flex-1" />
              <span className="text-xs text-muted-foreground w-24 text-right">金額</span>
              <span className="text-xs text-muted-foreground text-right" style={{minWidth: '120px'}}>科目</span>
              <button onClick={() => {
                if (selectedSimilar.size === similar.length) setSelectedSimilar(new Set());
                else setSelectedSimilar(new Set(similar.map((t: Transaction) => t.id)));
              }}
                className="text-xs text-primary hover:underline">
                {selectedSimilar.size === similar.length
                  ? (tr('Deselect All', '取消全選', '取消全选'))
                  : (tr('Select All', '全選', '全选'))}
              </button>
            </div>
            <div className="max-h-36 overflow-y-auto">
              {similar.map((t: Transaction) => (
                <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 cursor-pointer border-b border-muted/30 last:border-0">
                  <input type="checkbox"
                    checked={selectedSimilar.has(t.id)}
                    onChange={() => {
                      const next = new Set(selectedSimilar);
                      if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                      setSelectedSimilar(next);
                    }}
                    className="flex-shrink-0" />
                  <span className="text-xs text-muted-foreground w-14 flex-shrink-0">{t.transaction_date?.slice(5)}</span>
                  <span className="text-xs truncate flex-1 min-w-0">{t.description?.slice(0, 80)}</span>
                  <span className={`text-xs font-mono flex-shrink-0 w-24 text-right ${t.deposit_amount > 0 ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
                    {t.deposit_amount > 0 ? `+${t.deposit_amount.toLocaleString()}` :
                     t.withdrawal_amount > 0 ? `-${t.withdrawal_amount.toLocaleString()}` : ''}
                  </span>
                  <span className="text-xs flex-shrink-0 text-right min-w-[120px]">
                    {t.account_code ? (
                      <span className="bg-primary/10 text-primary px-1 py-0.5 rounded">
                        <span className="font-mono">{t.account_code}</span>
                        <span className="text-muted-foreground ml-1">
                          {accounts.find((a: any) => a.account_code === t.account_code)?.account_name?.slice(0, 12) || ''}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Apply button */}
        <button onClick={() => {
          if (selectedCode) onApply(selectedCode, false, selectedSimilar);
        }}
          disabled={!selectedCode}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-30 shrink-0">
          {selectedSimilar.size > 0
            ? (tr(`Apply to ${selectedSimilar.size + 1} transactions`, `套用科目（含 ${selectedSimilar.size} 筆相似交易）`, `套用科目（含 ${selectedSimilar.size} 笔相似交易）`))
            : (tr('Apply Account Code', '套用科目', '套用科目'))}
        </button>
      </div>
    </div>
  );
}

// ── Unified Link Document Modal (Invoice + Card Statement) ──
function LinkedDocModal({ txId, onClose, onLinkInvoices, onLinkCard }: {
  txId: string; onClose: () => void;
  /** Multi-invoice link — one payment settling several invoices (combined payment).
   *  `variance` is supplied when the selected total differs from the bank row. */
  onLinkInvoices?: (ids: string[], variance?: { amount: number; reason: string }) => void;
  onLinkCard: (csId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'invoice' | 'card'>('invoice');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reasonPreset, setReasonPreset] = useState('');
  const [reasonCustom, setReasonCustom] = useState('');

  // Fetch transaction details for context
  const { data: txData } = useQuery({
    queryKey: ['bank-transaction', txId],
    queryFn: () => api(`/bank-statements/transactions/${txId}`),
  });
  const tx = txData as any;

  const { data: invData } = useQuery({
    queryKey: ['unpaid-invoices', search],
    // Unpaid = reviewed (canonical settle lane): needs_review figures are not
    // postable and settled/voided are out; legacy draft/sent/overdue rows remap
    // onto reviewed via the DB migration.
    queryFn: () => api(`/invoices?status=reviewed${search ? `&q=${search}` : ''}`),
    enabled: tab === 'invoice',
  });
  const invoices = (invData?.data || []) as any[];

  const { data: cardData } = useQuery({
    queryKey: ['card-statements-list', search],
    queryFn: () => api(`/card-statements?q=${search}`),
    enabled: tab === 'card',
  });
  const cardStmts = (cardData?.data || []) as any[];

  const txAmount = tx ? (tx.deposit_amount || tx.withdrawal_amount || 0) : 0;
  const selectedInvoices = invoices.filter((inv: any) => selected.has(inv.id));
  const selectedTotal = Math.round(selectedInvoices.reduce((s: number, inv: any) => s + (inv.total || 0), 0) * 100) / 100;
  const selectedDelta = Math.round((txAmount - selectedTotal) * 100) / 100;
  const needsVarianceReason = varianceReasonRequired(selectedDelta);
  const varianceReason = resolveVarianceReason(reasonPreset, reasonCustom);
  const canLink = selected.size > 0 && (!needsVarianceReason || !!varianceReason);
  const toggleSelected = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl p-6 w-full max-w-7xl mx-4 h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold flex items-center gap-2 mb-3"><Link2 className="h-4 w-4" /> {tr('Link Document', '連結文件', '连结文件')}</h3>

        {/* Transaction context */}
        {tx && (
          <div className="bg-muted/50 rounded-lg p-3 mb-3 text-sm grid grid-cols-3 gap-2">
            <div><span className="text-muted-foreground">{tr('Description', '描述', '描述')}:</span> <span className="font-medium">{tx.description?.slice(0, 60)}</span></div>
            <div><span className="text-muted-foreground">{tr('Date', '日期', '日期')}:</span> {tx.transaction_date}</div>
            <div><span className="text-muted-foreground">{tr('Amount', '金額', '金额')}:</span> <span className="font-mono font-medium">HKD {(tx.deposit_amount || tx.withdrawal_amount || 0)?.toLocaleString()}</span></div>
          </div>
        )}

        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left: search + list */}
          <div className="w-1/2 space-y-3 overflow-y-auto">
            <div className="flex gap-1 border-b">
              <button onClick={() => setTab('invoice')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === 'invoice' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <FileText className="h-3.5 w-3.5 inline mr-1" />{tr('Invoices', '發票', '发票')}
              </button>
              <button onClick={() => setTab('card')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === 'card' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <CreditCard className="h-3.5 w-3.5 inline mr-1" />{tr('Card Statements', '信用卡月結單', '信用卡月结单')}
              </button>
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={tab === 'invoice' ? tr('Search invoices...', '搜尋發票...', '搜寻发票...') : tr('Search card statements...', '搜尋信用卡月結單...', '搜寻信用卡月结单...')}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm" />
            <div className="max-h-96 overflow-y-auto space-y-1">
              {tab === 'invoice' && invoices.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{tr('No unpaid invoices', '沒有未付款發票', '没有未付款发票')}</p>}
              {tab === 'invoice' && invoices.map((inv: any) => {
                const isSelected = selected.has(inv.id);
                const payee = inv.customer_name || inv.vendor_name || inv.supplier_name || '';
                return (
                <div key={inv.id} className={`flex items-center gap-2 rounded-lg ${isSelected ? 'bg-primary/10' : 'hover:bg-muted'}`}>
                  <label className="flex-1 flex items-start gap-2 px-3 py-2 cursor-pointer text-left min-w-0">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(inv.id)}
                      className="mt-1 shrink-0" onClick={e => e.stopPropagation()} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-medium text-sm">{inv.invoice_number || inv.id}</span>
                        <span className="text-xs text-muted-foreground font-mono">{inv.issue_date || '—'}</span>
                        <span className="ml-auto font-mono text-xs">{inv.currency || 'HKD'} {inv.total?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        {payee && <span className="text-xs text-muted-foreground truncate max-w-[45%]" title={payee}>{payee}</span>}
                        {inv.file_filename && (
                          <span className="text-xs text-muted-foreground/80 truncate inline-flex items-center gap-1" title={inv.file_filename}>
                            <FileText className="h-3 w-3 shrink-0" />{inv.file_filename}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                  {inv.file_id && (
                    <button onClick={() => setPreviewId(inv.file_id)}
                      className="p-1 pr-3 text-xs text-primary hover:underline shrink-0">{tr('View', '查看', '查看')}</button>
                  )}
                </div>
                );
              })}
              {tab === 'card' && cardStmts.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">{tr('No card statements', '沒有信用卡月結單', '没有信用卡月结单')}</p>}
              {tab === 'card' && cardStmts.map((cs: any) => (
                <div key={cs.id} className="flex items-center gap-2">
                  <button onClick={() => onLinkCard(cs.id)}
                    className="flex-1 flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted text-sm text-left">
                    <div>
                      <span className="font-medium">{cs.card_issuer || 'Card'}</span>
                      {cs.card_number_last4 && <span className="ml-2 text-muted-foreground">··{cs.card_number_last4}</span>}
                      <span className="ml-2 text-muted-foreground text-xs">{cs.statement_year}-{String(cs.statement_month || 1).padStart(2, '0')}</span>
                    </div>
                    <span className="font-mono text-xs">HKD {cs.closing_balance?.toLocaleString() || '-'}</span>
                  </button>
                  {cs.file_id && (
                    <button onClick={() => setPreviewId(cs.file_id)}
                      className="p-1 text-xs text-primary hover:underline shrink-0">{tr('View', '查看', '查看')}</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: PDF preview */}
          <div className="w-1/2 border-l pl-4 flex flex-col min-h-0">
            {previewId ? (
              <iframe src={`${WORKER_API_BASE}/file-storage/${previewId}/download?inline=1&token=${localStorage.getItem('token') || ''}${iframeClientParam()}`}
                className="w-full flex-1 border rounded" title="Document Preview" />
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                {tr('Select a document and click "View" to preview', '選擇文件並點擊"查看"以預覽', '选择文件并点击"查看"以预览')}
              </div>
            )}
          </div>
        </div>

        {/* Variance reason — the confirm endpoint refuses a mismatch without
            both variance_amount and review_reason. Ask here, before linking. */}
        {tab === 'invoice' && selected.size > 0 && needsVarianceReason && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-3 space-y-2">
            <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
              {tr(
                `Variance HKD ${selectedDelta.toLocaleString()} — a reason is required before linking`,
                `差額 HKD ${selectedDelta.toLocaleString()}——連結前必須提供原因`,
                `差额 HKD ${selectedDelta.toLocaleString()}——连结前必须提供原因`,
              )}
            </p>
            <select
              value={reasonPreset}
              onChange={e => setReasonPreset(e.target.value)}
              className="w-full px-2 py-1.5 border rounded text-sm bg-background"
            >
              <option value="">{tr('Select a reason…', '選擇原因…', '选择原因…')}</option>
              {VARIANCE_REASONS.map(reason => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
            {reasonPreset === 'Other' && (
              <input
                value={reasonCustom}
                onChange={e => setReasonCustom(e.target.value)}
                placeholder={tr('Type the reason', '輸入原因', '输入原因')}
                className="w-full px-2 py-1.5 border rounded text-sm bg-background"
                autoFocus
              />
            )}
          </div>
        )}

        {/* Selection bar — multi-invoice (combined payment) link */}
        {tab === 'invoice' && onLinkInvoices && (
          <div className="flex items-center justify-between gap-3 mt-3 px-3 py-2 border rounded-lg bg-muted/30">
            <div className="text-xs text-muted-foreground">
              {selected.size === 0 ? (
                tr('Tick invoices to link — one payment can settle several (combined payment)', '勾選發票以連結——一筆付款可結清多張（合併付款）', '勾选发票以连结——一笔付款可结清多张（合并付款）')
              ) : (
                <>
                  <span className="font-medium text-foreground">{tr('Selected', '已選', '已选')} {selected.size}</span>
                  {' · HKD '}{selectedTotal.toLocaleString()}
                  <span className={`ml-2 font-medium ${Math.abs(selectedDelta) < 0.02 ? 'text-green-900 dark:text-green-200' : 'text-amber-900 dark:text-amber-100'}`}>
                    {Math.abs(selectedDelta) < 0.02
                      ? '✓ ' + tr('matches transaction', '與交易相符', '与交易相符')
                      : `${selectedDelta > 0 ? '+' : ''}${selectedDelta.toLocaleString()} ${tr('vs transaction', '與交易差額', '与交易差额')}`}
                  </span>
                  <span className="block text-[10px] text-muted-foreground mt-0.5">
                    {tr(
                      'On link, the COA changes to Accounts Receivable / Accounts Payable',
                      '連結後，會計科目將改為應收賬款／應付賬款',
                      '连结后，会计科目将改为应收账款／应付账款',
                    )}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {selected.size > 0 && (
                <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground underline">
                  {tr('Clear', '清除', '清除')}
                </button>
              )}
              <button
                disabled={!canLink}
                onClick={() => onLinkInvoices(
                  [...selected],
                  needsVarianceReason ? { amount: selectedDelta, reason: varianceReason } : undefined,
                )}
                className="px-4 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-30">
                {selected.size > 1
                  ? tr(`Link ${selected.size} Invoices`, `連結 ${selected.size} 張發票`, `连结 ${selected.size} 张发票`)
                  : tr('Link Selected', '連結所选', '连结所选')}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-muted">{tr('Cancel', '取消', '取消')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Auto-Match Review Modal ──
// ── Card Match Review Modal ──
function CardMatchReviewModal({ matches, onConfirm, onReject, onClose }: {
  matches: any[];
  onConfirm: (txId: string, csId: string) => void;
  onReject: (txId: string) => void;
  onClose: () => void;
}) {
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [rejected, setRejected] = useState<Set<string>>(new Set());

  const pending = matches.filter(m => !confirmed.has(m.transaction_id) && !rejected.has(m.transaction_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl p-6 w-full max-w-2xl mx-4 space-y-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-purple-600" />
            Card Match Suggestions ({confirmed.size + rejected.size}/{matches.length})
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
        </div>

        {pending.length === 0 ? (
          <div className="text-center py-8 space-y-2">
            <CheckCircle2 className="h-8 w-8 text-green-900 dark:text-green-200 mx-auto" />
            <p className="text-sm font-medium">{tr('All suggestions reviewed!', '所有建議已審核！', '所有建议已审核！')}</p>
            <button onClick={onClose} className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm">
              {tr('Close', '關閉', '关闭')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {tr('Review each match. Confirm to link the bank transaction to a card statement.', '審核每個配對。確認後將銀行交易連結至信用卡月結單。', '审核每个配对。确认后将银行交易连结至信用卡月结单。')}
              </span>
              <button
                onClick={() => {
                  pending.forEach(m => {
                    onConfirm(m.transaction_id, m.card_statement_id);
                    setConfirmed(prev => new Set(prev).add(m.transaction_id));
                  });
                }}
                className="px-3 py-1.5 bg-green-800 text-white rounded text-xs font-medium hover:bg-green-900"
              >
                {tr('Accept All', '全部接受', '全部接受')} ({pending.length})
              </button>
            </div>
            <div className="space-y-2 overflow-y-auto flex-1">
              {pending.map(m => (
                <div key={m.transaction_id} className="border rounded-lg p-3 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        m.confidence === 'high' ? 'bg-green-100 text-green-700' :
                        m.confidence === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>{m.confidence?.toUpperCase() || 'MEDIUM'}</span>
                      <span className="text-sm font-medium truncate">{m.card_issuer}</span>
                      {m.card_last4 && <span className="text-xs text-muted-foreground">*{m.card_last4}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{m.reason}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => {
                      onConfirm(m.transaction_id, m.card_statement_id);
                      setConfirmed(prev => new Set(prev).add(m.transaction_id));
                    }}
                      className="px-3 py-1.5 bg-green-800 text-white rounded text-xs font-medium hover:bg-green-900">
                      ✓ Confirm
                    </button>
                    <button onClick={() => {
                      onReject(m.transaction_id);
                      setRejected(prev => new Set(prev).add(m.transaction_id));
                    }}
                      className="px-3 py-1.5 border border-red-300 text-red-900 dark:text-red-200 rounded text-xs hover:bg-red-50">
                      ✗ Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Pending Review Banner: shows draft statements awaiting confirmation ──
function PendingReviewBanner() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['bank-statements-drafts'],
    queryFn: () => api('/bank-statements?only_drafts=1'),
    refetchInterval: 5000, // poll every 5s so newly uploaded drafts appear quickly
  });
  const drafts: any[] = data?.data || [];
  const dismissMut = useMutation({
    mutationFn: (id: string) => api(`/bank-statements/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-statements-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
    },
    onError: (err: any) => {
      if (err?.status === 403 || /higher permission/i.test(err?.error || err?.message || '')) {
        toast.info('Only account owner or boss-level users can discard drafts. Please ask your admin.');
      } else {
        toast.info(`Discard failed: ${err?.error || err?.message || 'Unknown error'}`);
      }
    },
  });
  if (drafts.length === 0) return null;
  return (
    <div className="rounded-lg border-2 border-black bg-gray-100 dark:bg-gray-800 p-4 space-y-2">
      <div className="flex items-start gap-3">
        <div className="text-2xl">⚠️</div>
        <div className="flex-1">
          <h3 className="font-bold text-black dark:text-white">
            {drafts.length} statement{drafts.length === 1 ? '' : 's'} pending review
          </h3>
          <p className="text-sm text-black dark:text-gray-300">
            The system extracted data from your uploaded file{drafts.length === 1 ? '' : 's'}.
            Please review and confirm before saving to the database.
          </p>
        </div>
      </div>
      <div className="space-y-1 pt-2">
        {drafts.map((d: any) => (
          <div
            key={d.id}
            className="flex items-center justify-between rounded border border-black bg-white dark:bg-gray-700 px-3 py-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <a
              href={`/bank-statements/review/${d.id}`}
              className="flex-1 text-sm"
            >
              <span className="font-medium">{d.bank_name || 'Statement'}</span>
              {d.account_number && <span className="text-muted-foreground"> · {d.account_number}</span>}
              {d.period_start && <span className="text-muted-foreground"> · {d.period_start} → {d.period_end}</span>}
            </a>
            <div className="flex items-center gap-2">
              <a
                href={`/bank-statements/review/${d.id}`}
                className="text-sm text-black dark:text-white font-medium hover:underline"
              >
                Review →
              </a>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (confirm(tr('Discard this draft? It will be moved to the Recycle Bin (30-day restore) and the PDF will also be removed from File Storage.', '放棄此草稿？將移至回收站（可在30天內還原），PDF 也將從文件存儲中刪除。', '放弃此草稿？將移至回收站（可在30天內还原），PDF 也將從文件存儲中删除。'))) {
                    dismissMut.mutate(d.id);
                  }
                }}
                disabled={dismissMut.isPending}
                className="text-xs px-2 py-1 border border-red-300 text-red-900 dark:text-red-200 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                title="Discard this draft"
              >
                🗑 Discard
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
